import React from 'react';

import { Plural, Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import makeStyles from '@mui/styles/makeStyles';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import Checkbox from '@mui/material/Checkbox';
import ClosedCaptionIcon from '@mui/icons-material/ClosedCaption';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import useInterval from '../../hooks/useInterval';
import AirchainAPI from '../../utils/airchain-api';
import { CC1_LANGUAGES_BY_PROVIDER, CC3_LANGUAGES } from '../../utils/caption-languages';
import Paper from '../../misc/Paper';
import PaperHeader from '../../misc/PaperHeader';
import Select from '../../misc/Select';

// Speechmatics' own confirmed real-time-supported audio event types (see
// speechmatics_client.py's AUDIO_EVENT_TYPES) -- a closed set, not a
// freeSolo tag list like speechmatics_additional_vocab. speechmaticsAudioEventTypeLabel
// (below, inside the component) maps each value to its translated display label.
const SPEECHMATICS_AUDIO_EVENT_TYPES = ['laughter', 'applause', 'music'];

// Same stack global.js already uses for its one other fixed-width block of
// text (the textarea) rather than a second, competing choice -- the caption
// preview below needs real 32-column alignment, which is the one place
// DESIGN.md's "Roboto for all data" cannot apply.
const MONOSPACE_FONT_FAMILY =
	'Consolas, "Andale Mono WT", "Andale Mono", "Lucida Console", "Lucida Sans Typewriter", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Liberation Mono", "Nimbus Mono L", Monaco, "Courier New", Courier, monospace';

// Additive panel, not part of upstream restreamer-ui: turns live CEA-608/708
// captioning on/off for this channel. Structurally mirrors Egress.js (icon +
// label + Switch, same state->color convention) since this reads as "another
// thing attached to this channel," not a distinct Airchain-branded surface --
// see the shape brief this was built from. Configuration opens from the
// header's own icon slot (PaperHeader's onEdit), the same place and the
// same pencil every other card on this page uses -- it used to be a gear
// 8px from the switch that stops captions on a live broadcast.
const useStyles = makeStyles((theme) => ({
	captionsBar: {
		'& .captions-left': {
			minHeight: 24,
			minWidth: 30,
			marginRight: 6,
		},
		'& .captions-name': {
			fontSize: '.9rem',
		},
		'& .captions-detail': {
			fontSize: '.75rem',
			color: theme.palette.text.secondary,
		},
		// Real modifiers, at the same two-class specificity as the rule
		// above: color="warning.main"/color="error" on these Typographies
		// lost to it silently, so the one message that means "stop and
		// look before you save" rendered in the same grey as ordinary
		// help text.
		'& .captions-detail.is-warning': {
			color: theme.palette.warning.main,
		},
		'& .captions-detail.is-error': {
			color: theme.palette.error.main,
		},
		// Subordinate to the status line by size and colour, and monospace
		// only on the code itself so "Ref:" stays prose. userSelect is the
		// whole point of the line -- see its comment at the call site.
		'& .captions-ref': {
			fontSize: '.7rem',
			color: theme.palette.text.hint,
			'& span': {
				fontFamily: 'monospace',
				userSelect: 'all',
			},
		},
		// The state icon carries the severity the Switch cannot: MUI
		// paints a Switch's color only while it is checked, and checked
		// is false for both "off" and "error", so those two states were
		// pixel-identical on the page.
		'& .captions-state-icon.is-live': {
			color: theme.palette.secondary.main,
		},
		'& .captions-state-icon.is-warning': {
			color: theme.palette.warning.main,
		},
		'& .captions-state-icon.is-error': {
			color: theme.palette.error.main,
		},
		'& .captions-state-icon.is-off': {
			color: theme.palette.text.secondary,
		},
	},
	// State, switch and the live caption preview stay put while the form
	// below them scrolls: they are the three things that say whether the
	// broadcast is currently fine, and they used to leave the screen
	// exactly while the operator was editing what they display.
	statusSticky: {
		position: 'sticky',
		top: 0,
		zIndex: 3,
		backgroundColor: theme.palette.background.paper,
	},
	settingsArea: {
		marginTop: '.6em',
		paddingTop: '.8em',
		borderTop: `1px solid ${theme.palette.background.light1}`,
	},
	// The form itself is the only part that scrolls. Full height with
	// Gemini selected and Advanced open runs past 1400px, well beyond a
	// 1080p viewport, which put Save out of reach below and the status
	// line out of reach above.
	settingsScroll: {
		maxHeight: 'min(60vh, 640px)',
		overflowY: 'auto',
	},
	actionBar: {
		position: 'sticky',
		bottom: 0,
		zIndex: 3,
		marginTop: '.6em',
		paddingTop: '.6em',
		backgroundColor: theme.palette.background.paper,
		borderTop: `1px solid ${theme.palette.background.light1}`,
		// The two action labels measured 3.24:1 against this bar, because
		// the state they sit in whenever nothing is unsaved is MUI's own
		// disabled Button colour -- text.disabled (38% white) over
		// background.button_disabled (#333333). An inert button still has
		// to be readable: Save and Revert are how an operator knows what
		// the bar can do at all before deciding what to change. This is a
		// two-class-target rule, same specificity trick as
		// .captions-detail.is-warning above -- the theme's own
		// `&.Mui-disabled` rule is (0,2,0) and this is (0,3,0), so it wins
		// without !important. text.secondary (70% white) measures 7.1:1 on
		// that fill and stays visibly dimmer than the enabled label, which
		// is pure white.
		'& .MuiButton-outlined.Mui-disabled': {
			color: theme.palette.text.secondary,
		},
	},
	// 0.7rem uppercase with a hairline above, matching PaperHeader's own
	// treatment: variant="h3" (Dosis 1.35rem) made "Caption timing"
	// outrank both the card title and the health signal.
	sectionLabel: {
		fontSize: '.7rem',
		fontWeight: 500,
		textTransform: 'uppercase',
		letterSpacing: '.08em',
		color: theme.palette.text.secondary,
		paddingTop: '.6em',
		borderTop: `1px solid ${theme.palette.background.light1}`,
		// The first group needs no rule of its own: settingsArea already
		// draws one directly above it.
		'&.first': {
			paddingTop: 0,
			borderTop: 'none',
		},
	},
	// Vendor SKUs alone ("Deepgram", "Speechmatics") make the operator
	// translate "fastest / labels speakers / supports my language" into a
	// procurement decision. The note is decision support while the menu is
	// open; the closed field shows the name only (see renderValue below).
	providerOption: {
		width: '100%',
		justifyContent: 'space-between',
		'& .provider-name': {
			fontSize: '.9rem',
		},
		'& .provider-note': {
			fontSize: '.7rem',
			color: theme.palette.text.secondary,
		},
		'& .provider-check': {
			color: theme.palette.secondary.main,
		},
	},
	// The live caption preview (see the on_screen block below). Its own
	// surface -- the same token global.js gives the textarea, the one
	// other fixed-width block of text in the product -- so the rows read
	// as a window onto the broadcast. It used to wear background.dark2,
	// which is what outlinedInput.js fills a DISABLED input with: the most
	// valuable live element in the panel in the "switched off" colour. No
	// shadow, per DESIGN.md: depth here comes from the surface, not from
	// casting light.
	captionPreview: {
		marginTop: '.6em',
		padding: '.5em .6em',
		borderRadius: 4,
		backgroundColor: theme.palette.background.modalbox,
		'& .caption-preview-cc': {
			fontSize: '.65rem',
			lineHeight: 1.6,
			letterSpacing: '.04em',
			padding: '0 .35em',
			border: `1px solid ${theme.palette.background.light1}`,
			borderRadius: 2,
			color: theme.palette.text.secondary,
		},
		'& .caption-preview-lang': {
			fontSize: '.7rem',
			color: theme.palette.text.secondary,
		},
		// 32ch wide because that is literally what a CEA-608 row is (see
		// caption_injector.py's --chars-per-line): the block holds its
		// width whatever the current caption says, so text doesn't jump
		// sideways every reveal. whiteSpace: pre keeps the rows exactly as
		// the injector sent them, leading spaces and all.
		'& .caption-preview-rows': {
			fontFamily: MONOSPACE_FONT_FAMILY,
			fontSize: '.8rem',
			lineHeight: 1.45,
			letterSpacing: '.02em',
			fontVariantNumeric: 'tabular-nums',
			whiteSpace: 'pre',
			width: '32ch',
			// A fallback font that resolves wider than Consolas makes 32ch
			// exceed this column; scroll the block, don't overflow the card.
			overflowX: 'auto',
			color: theme.palette.text.primary,
		},
	},
}));

// pendingAction === 'disable' wins over $status.state entirely while set.
// This isn't just cosmetic: get_status()'s stt read and handleSwitch's own
// DisableCaptions call are two independent requests to core, and even with
// useInterval's poll paused while pending (see its own $pending-gated delay
// below), a poll that was ALREADY in flight the instant the operator clicked
// can still land mid-flight during core's blocking process stop and catch
// the stt process in a transitional exec state (confirmed live: core's
// DELETE handler blocks on ffmpeg.Stop(true) before removing the task, so a
// concurrent GET during that window sees the process still there, briefly
// in "killed" -- indistinguishable from a real crash to get_status(), see
// its own finishing/finished comment for the same class of issue). We
// already know with certainty this is a deliberate stop the instant the
// operator clicks the switch -- no need to ask core's transient state to
// confirm it.
// Two lines and an icon, not one sentence: state on line 1 (short enough
// that it never wraps in this column) and the explanation on line 2, in
// the .captions-detail style the stylesheet already defined and the status
// block never used. The icon carries severity because the Switch cannot --
// MUI paints its colour only while checked, and checked is false for both
// "off" and "error", so a crashed captioner looked exactly like one the
// operator turned off themselves.
//
// detail (a core process exec state, e.g. "failed") stays out of the label
// entirely -- PRODUCT.md's "hide the machinery" -- and goes on the row's
// title attribute instead, so support can read it without devtools.
// The four stages this pipeline can fail in (demux/inject/stt/delay, see
// restreamer_captions.get_status) are our own process graph, and naming
// them bought the operator nothing: every one of them ends in the same
// recovery action, and "video pipeline failed" reads as "your video is
// gone" -- false, and an anxiety spike on a panel whose job is calm under
// live pressure. What actually matters at 03:00 is the opposite fact: the
// programme is still going out, only the captions stopped. The raw code
// stays, selectable, as a support reference.
function statusPresentation(state, pendingAction, detail) {
	if (pendingAction === 'disable') {
		return { label: <Trans>Turning off…</Trans>, detail: null, Icon: ClosedCaptionIcon, tone: 'off' };
	}
	switch (state) {
		case 'starting':
			return { label: <Trans>Starting captions…</Trans>, detail: null, Icon: ClosedCaptionIcon, tone: 'warning' };
		case 'live':
			// No second line: while live the caption preview below is the
			// detail, and it says more than any sentence could.
			return { label: <Trans>Captions live</Trans>, detail: null, Icon: ClosedCaptionIcon, tone: 'live' };
		case 'idle':
			// Not an error and not off: the supervisor paused this channel
			// because nothing is being sent to it (see control_service.py's
			// idle_pause_seconds). The toggle stays on -- the operator
			// never turned it off -- so the label has to say what is
			// actually true rather than borrow either neighbouring state.
			// It must NOT promise a resume: auto-resume isn't built yet
			// (see restreamer_captions.py's clear_auto_paused), so an
			// operator is the only thing that restarts this channel.
			return {
				label: <Trans>Waiting for source</Trans>,
				detail: <Trans>Paused because nothing is arriving. Switch off, then on, to restart.</Trans>,
				Icon: PauseCircleOutlineIcon,
				tone: 'warning',
			};
		case 'error':
			return {
				label: <Trans>Captions stopped</Trans>,
				detail: <Trans>Your video is unaffected. Switch on to restart.</Trans>,
				Icon: ErrorOutlineIcon,
				tone: 'error',
			};
		default:
			return { label: <Trans>Captions off</Trans>, detail: null, Icon: ClosedCaptionIcon, tone: 'off' };
	}
}

// What GetCaptionsStatus reported as currently on screen, per caption
// channel, lowest CC number first -- {"1": {rows, lang, mode, age_seconds}}
// (see control_service.py's _caption_preview, reading caption_injector.py's
// /health on_screen). No second API call: this rides on the once-a-second
// status poll this panel already runs.
//
// Channels with no rows are dropped rather than drawn empty: a live channel
// during a pause in speech genuinely has nothing on screen, and an empty
// monospace block reads as a caption that lost its text. The caller shows
// its own "waiting" line when this comes back empty.
function onScreenChannels(onScreen) {
	return Object.entries(onScreen || {})
		.filter(([, channel]) => (channel.rows || []).length > 0)
		.sort(([a], [b]) => Number(a) - Number(b));
}

// What this channel's own labeller has actually said, per caption channel --
// {"1": {current, changes, labelled}} from caption_injector.py's /health
// `speakers`, riding along on the same status poll as on_screen above (see
// control_service.py's t_captions_status). `current` is the last speaker id
// the injector saw and `changes` counts the times it actually changed:
// exactly the events that insert the ">>" speaker marker.
//
// That marker is diarization's only OTHER visible output, which is the whole
// reason this readout exists. On single-speaker content the label never
// changes, so the marker never appears, and an operator who has just
// switched "Local diarization (self-hosted)" on sees no evidence whatsoever
// that it is running. "S2, 0 changes" is the honest reading of exactly that
// state: the labeller is running, and the same speaker has been talking
// since the channel went live.
//
// Absent -- not false, not empty -- from an injector that predates the
// field, and that is deliberately not the same as "no speaker": the caller
// renders nothing at all for it. Sorted lowest CC number first, like
// onScreenChannels above.
function speakerChannels(speakers) {
	if (!speakers) {
		return null;
	}
	return Object.entries(speakers).sort(([a], [b]) => Number(a) - Number(b));
}

// The injector's own average caption latency over its last window --
// {mean_s, samples, window_s}, riding the same once-a-second status poll as
// on_screen and speakers above (see control_service.py's t_captions_status,
// which passes it through from caption_injector.py's /health verbatim).
//
// This is the one number on this panel that is LATENCY rather than freshness.
// on_screen's age_seconds is stamped when a caption is revealed, so it answers
// "has text appeared recently"; this answers "how far behind the live audio is
// that text" -- the question the operator was actually asking when they read
// the old line as a latency. It is the injector's own measurement, never
// re-derived here: no clock of this panel's and no arithmetic on age_seconds
// can produce it.
//
// null -- never 0 -- when the response carries no caption_latency at all (an
// injector older than the field), when it carries null (a live injector with
// no sample in its window yet), or when it carries a sample count of zero.
// All three are "nothing was measured", and the caller falls back to its own
// age line rather than printing a 0.0s that would read as a perfect result
// nobody observed.
//
// mean_s is SIGNED, and negative is normal in this pipeline: it means the
// caption text arrived AHEAD of the display slot it was placed in. Nothing
// here clamps it, abs()es it or otherwise repairs the sign -- the caller
// decides what to say about a value at or below zero, this only reports it.
function captionLatency(latency) {
	if (!latency || typeof latency !== 'object') {
		return null;
	}
	if (typeof latency.mean_s !== 'number' || !(latency.samples > 0)) {
		return null;
	}
	return {
		mean: latency.mean_s,
		// The window is the injector's, not a constant of this panel's -- it
		// is what the mean is an average OVER, so the sentence below has to
		// quote the one the measurement was actually taken over. 30 is only
		// the fallback for a payload that states a mean and a sample count
		// but no window: never a second guess at the measurement itself.
		window: typeof latency.window_s === 'number' ? latency.window_s : 30,
	};
}

// The mean, to one decimal and nothing else: this is an average over a window
// of many samples, so a second digit would be precision nobody acts on and a
// rounded-away integer would be a number that looks exact. The "s" lives in
// the sentence, not here, so a translation can place the unit where its own
// grammar needs it.
function formatLatencyMean(seconds) {
	return seconds.toFixed(1);
}

// At or below this the average is not a lag an operator can act on, and it is
// also where this pipeline actually sits while it is keeping pace: the mean
// lands at ~0 -- and routinely BELOW zero, since text can be ready before the
// slot it is displayed in, see captionLatency's sign note. The panel says the
// captions are keeping up instead of printing "0.0s" (which reads as a
// suspiciously perfect measurement) or a negative "latency" (which reads as a
// bug in the panel). Deliberately a threshold on the MEAN only: it decides
// which sentence to show, and never substitutes a value for the measurement.
const KEEPING_UP_SECONDS = 0.05;

// "CC1"/"CC3", or "Service 1"/"Service 2" for the CEA-708 services, which
// arrive as their own 100+n channel numbers. One definition for the
// on-screen rows and the speaker readout both: the injector keys both by the
// same channel number, so they can never disagree about which channel a line
// belongs to.
function captionChannelLabel(number) {
	const value = Number(number);
	return value >= 103 ? `Service ${value - 100}` : `CC${number}`;
}

// What this channel actually puts out, derived from the same settings fields
// the Delivered formats controls write -- no second source of truth, no API
// call of its own. CEA-608 is emitted unless disable_608 says otherwise (the
// stored flag is a DISABLE flag: false == emitted, see DEFAULT_SETTINGS),
// CEA-708 the same, and the WebVTT track is an additional delivery mode
// rather than a replacement (see webvtt_captions_enabled above). All three
// are reported, on or off: a format that is switched off is a fact about the
// channel, not something to leave out and let the reader assume.
//
// open_captions_enabled -- the other delivery mode on the server's own
// JobConfig -- is deliberately NOT read here: this panel has no control for
// it and never loads it, so naming it would be a claim about a channel this
// component has not actually looked at.
function deliveredFormats(settings) {
	return [
		{ label: 'CEA-608', on: !settings.disable_608 },
		{ label: 'CEA-708', on: !settings.disable_708 },
		{ label: 'WebVTT', on: !!settings.webvtt_captions_enabled },
	];
}

// The heartbeat's own read of the same on-screen payload the preview draws:
// the newest channel that reports a real age_seconds, and that age. The text
// of its last row is returned only as the truth test for "is there a caption
// on screen at all" -- it is NOT rendered by the heartbeat, because the
// preview directly below already draws those words and repeating them reads
// as a duplicate. Only the
// injector's own age_seconds is used, never a clock of this panel's: the
// value rides the once-a-second status poll, so a poll that lands early or
// late cannot make a caption look older than the injector says it is.
//
// null when the payload carries no age at all -- no configured channel, or
// an injector too old to report it. The caller then says nothing rather than
// inventing a zero and calling it "0.0s ago".
function lastCaption(onScreen) {
	const channels = Object.values(onScreen || {}).filter((channel) => typeof channel.age_seconds === 'number');
	if (channels.length === 0) {
		return null;
	}
	const newest = channels.reduce((a, b) => (b.age_seconds < a.age_seconds ? b : a));
	const rows = (newest.rows || []).filter((row) => row && row.trim());
	return {
		age: newest.age_seconds,
		text: rows.length > 0 ? rows[rows.length - 1].trim() : null,
	};
}

// 0.8s while the caption is still "just now", whole seconds once it is not:
// "Last caption 42.3s ago" is precision nobody acts on, and the sub-second
// digit is the whole point of the faintest case (a live channel that is
// captioning continuously).
function formatAge(seconds) {
	return seconds < 10 ? `${seconds.toFixed(1)}s` : `${Math.round(seconds)}s`;
}

function switchColor(state, pendingAction) {
	if (pendingAction === 'disable') {
		return 'secondary';
	}
	switch (state) {
		case 'starting':
			return 'warning';
		case 'error':
			return 'error';
		default:
			return 'secondary';
	}
}

// Maps a previously-saved language code onto the current dropdown's valid
// options -- exact match first, then a base-language match (e.g. a
// leftover "es-ES" from before cc3_lang switched to Google Translate's own
// bare "es" codes), else the list's first entry, never leaving a picker
// pointed at a value with no matching option (which just renders blank --
// not an error, but a confusing one: the field looks unset even though
// real saved data exists).
//
// Returns { code, changed }, not just the code: an exact or base-language
// match is still genuinely the SAME language the operator picked, just
// reformatted for the new provider's code style -- safe to apply silently.
// Falling all the way to validCodes[0] is not the same language at all,
// an arbitrary substitution the operator never chose -- callers must
// treat changed=true as something to surface, not swallow. This
// distinction is the fix for a real gap an isolated design review caught:
// switching transcription_provider while captions are LIVE (both fields
// are in LIVE_EDITABLE_FIELDS) could previously push a silently-different
// caption language onto an actual broadcast with zero feedback.
function resolveLangCode(code, validCodes) {
	if (validCodes.includes(code)) {
		return { code, changed: false };
	}
	const base = (code || '').split('-')[0].toLowerCase();
	const baseMatch = validCodes.find((candidate) => candidate.split('-')[0].toLowerCase() === base);
	if (baseMatch) {
		return { code: baseMatch, changed: false };
	}
	return { code: validCodes[0], changed: true };
}

// cc3_lang's empty string ('') is the API's own "disabled"
// value (see JobConfig.cc3_lang / _injector_args' `if cfg.cc3_lang`).
// all_caps/start_row are later additions, not part of the original
// settings-picker brief -- included here (unlike the Advanced settings
// fields below) since they're mainstream, self-explanatory broadcast-
// captioning conventions an operator would expect to control directly
// (row position is real CEA-608 terminology this audience already knows),
// the same tier as caption_mode/roll_up_rows rather than a power-user field.
const DEFAULT_SETTINGS = {
	transcription_provider: 'deepgram',
	cc1_lang: 'en-US',
	cc3_lang: '',
	caption_tracks: [],
	// Which output slot the recognized language (cc1_lang) is sent to:
	// 'cc1' = CC1 + CEA-708 Service 1, 'cc3' = CC3 + Service 2. The slots
	// are fixed pairs, so a second language always takes the other one --
	// see caption_injector.py's --primary-channel. 'cc1' is the historical
	// layout. Which caption FORMATS leave the channel alongside each other
	// is the pair below, not this field.
	primary_caption_channel: 'cc1',
	// The two format gates behind the "Delivered formats" pair. Stored as the
	// DISABLE flags control_service.py's JobConfig declares (disable_608 /
	// disable_708 -- false == emitted), and shown as POSITIVE controls
	// (checked == the captions are going out), because "captions on" is the
	// question an operator is actually asking. Both read and write invert
	// exactly once, at the checkbox (see handleCaptionFormatToggle); the
	// wire format stays the server's own, so an API caller, this panel and a
	// value persisted by a broadcaster's own tooling
	// (disable_708 exists for exactly that -- a downstream 708 encoder of
	// their own) all mean the same thing by the same key. That last case is
	// why the polarity is not reversed in DEFAULT_SETTINGS or at save: a
	// stored true has to keep reading as "off" here.
	//
	// DELIBERATELY ASYMMETRIC, and deliberately STRICTER than the server.
	// CEA-608 rides the video as line-21 carriage that is wired when the
	// injector process starts, so control_service.py's needs_full_reprovision
	// lists disable_608: change it against a running channel and the next
	// ENABLE takes the full teardown + fresh-provision path.
	//
	// Note what the server does NOT do: disable_608 is also in
	// INJECTOR_LIVE_UPDATABLE_FIELDS, and the live-save 409 check subtracts
	// that set -- so a live save carrying it is hot-applied, not rejected.
	// The lock here is therefore an operator-facing choice, not a server
	// requirement: it keeps the running chain and the stored flag from
	// drifting apart mid-broadcast, which is what a re-enable would resolve
	// anyway, and it avoids a greyed-or-live control whose behaviour depends
	// on which path last touched the channel. Decide it on purpose if you
	// change it.
	//
	// CEA-708 rides as GA94 SEI the running injector can gate on its next
	// emit, so disable_708 is in INJECTOR_LIVE_UPDATABLE_FIELDS and NOT in
	// needs_full_reprovision. Mirroring that here -- disable_708 in
	// LIVE_EDITABLE_FIELDS below, disable_608 not -- is what locks the 608
	// control while captions are live (fieldLockedWhileLive) and keeps it
	// out of a live save's diff.
	disable_608: false,
	disable_708: false,
	caption_mode: 'pop-on',
	roll_up_rows: 2,
	start_row: 15,
	all_caps: false,
	// WEBVTT_CAPTIONS_PLAN.md: an independent, additional delivery mode --
	// a real toggleable subtitle track in the viewer's own player, on top
	// of the closed captions (CEA-608/708) this panel already produces,
	// not a replacement for them. Deliberately not in LIVE_EDITABLE_FIELDS
	// below (see control_service.py's own needs_full_reprovision -- this
	// changes which processes exist for the channel, same class of field
	// as transcription_provider itself).
	webvtt_captions_enabled: false,
	// Speechmatics-only (see control_service.py's JobConfig.speechmatics_max_delay
	// -- Speechmatics' own documented valid range is 0.7-4.0s). Was a
	// hardcoded speechmatics_client.py CLI default (2.5s) with no way to
	// change it per-channel; exposed here specifically because latency is
	// a real, operator-facing tradeoff (faster captions vs. more revisions
	// visibly correcting themselves), unlike the other Speechmatics-only
	// fields (diarize/max_speakers/additional_vocab) which live in the
	// Advanced settings accordion below instead.
	speechmatics_max_delay: 2.5,

	// google_batch_diarized-only (see control_service.py's JobConfig
	// fields of the same name). Unlike every other provider here, this one
	// is not real-time -- selecting it deliberately trades this panel's
	// normal ~5s caption latency for a much longer, tunable delay in
	// exchange for real per-speaker diarization (DIARIZATION_PLAN.md).
	// Surfaced as its own "Speaker diarization" section in Advanced
	// settings (below) rather than folded into "Recognition tuning" like
	// the other providers' fields, since getting the delay wrong doesn't
	// just mis-tune recognition quality -- it can mean captions silently
	// never appear (see google_batch_client.py's own deadline-drop logic).
	google_batch_delay_seconds: 35.0,
	google_batch_chunk_seconds: 20.0,
	google_batch_safety_margin_seconds: 8.0,

	// Hold the stream (video and audio) this many seconds so a streaming
	// provider's captions line up with the picture -- the same delay stage
	// google_batch_diarized uses, which is why that provider does not show
	// this field (its own "Caption delay" is the same setting). 0 = off.
	// Reprovision-class: deliberately absent from LIVE_EDITABLE_FIELDS (see
	// JobConfig.video_delay_seconds).
	video_delay_seconds: 0,

	// Card #54 / DIARIZATION_PLAN.md: speaker labels from a diarizer running
	// on this very stack, for the providers whose own streaming recognition
	// returns no speaker information at all (see LOCAL_DIARIZATION_PROVIDERS
	// below). Off by default -- it is a separate host-local process most
	// channels never need. Reprovision-class, exactly like
	// google_isolate_vocals above and for the same reason: flipping it starts
	// or stops a process in the channel's caption path rather than re-tuning
	// one that is already running, so control_service.py's
	// needs_full_reprovision compares BOTH fields against active_config_json
	// and takes the full teardown + fresh-provision path (a live save that
	// carries either one is a 409). Both are therefore deliberately NOT in
	// LIVE_EDITABLE_FIELDS below -- and that single omission is also what
	// locks both controls while captions are live, so their own help text is
	// what tells the operator how to change them.
	local_diarize: false,
	// Where that diarizer listens, as the Core container reaches it, e.g.
	// 192.168.139.3:8791 (the host's OrbStack bridge; 16 kHz mono PCM up,
	// label/turn/eos events back). Only meaningful while local_diarize is
	// true. Empty means "this stack's diarizer": the server fills in
	// AIRCHAIN_LOCAL_DIARIZER_ENDPOINT, and does the same for a loopback
	// address, which inside the container would be the container itself
	// (see restreamer_captions._resolve_local_diarizer_endpoint).
	local_diarize_endpoint: '',

	// Advanced settings (see the Advanced settings accordion below). All
	// of these already existed end-to-end on the backend -- JobConfig,
	// STT_ONLY_HOT_SWAP_FIELDS/INJECTOR_LIVE_UPDATABLE_FIELDS, the actual
	// STT clients -- with no UI control until now (see the confirmed
	// design brief this section was built from). Deepgram has no fields
	// of its own here; the accordion shows a plain "nothing to tune yet"
	// message for it instead of empty controls.
	google_boost_phrases: [],
	google_boost_value: 15.0,
	google_profanity_filter: false,
	google_enable_vad: false,
	// Reprovision-class, like webvtt_captions_enabled above: turning it on or
	// off inserts/removes a whole voice-isolation process in the channel's own
	// audio chain ahead of the recognizer, which cannot be done to a channel
	// that is already live. control_service.py's
	// needs_full_reprovision compares this against active_config_json and
	// takes the full teardown + fresh-provision path instead of the cheap STT
	// resume branch (a live save that carries it is a 409), which is exactly
	// why it is NOT in LIVE_EDITABLE_FIELDS below -- that omission is also
	// what locks the checkbox while captions are live, so the control's own
	// help text is what tells the operator how to change it. Applies to the
	// google and gemini providers only -- not google_batch_diarized, whose
	// pipeline has no isolator stage. The engine behind it is a
	// stack-wide server setting (VOICE_ISOLATOR_BACKEND), not a per-channel
	// choice, so this stays a simple on/off.
	google_isolate_vocals: false,
	// Shared by both Google-based providers (transcription_provider
	// 'google' AND 'google_batch_diarized') -- RecognitionConfig.
	// denoiser_config lives on the request's top-level config, not inside
	// RecognitionFeatures, so it isn't scoped to just the real-time
	// provider the way boost_phrases/profanity_filter/enable_vad are. See
	// the "Audio processing" section below (its own shared conditional,
	// not duplicated into both providers' sections).
	google_enable_denoise: false,
	google_denoise_snr_threshold: 0.0,
	// gemini-only (see control_service.py's JobConfig fields of the same
	// names). None of the google_* fields above apply to this provider --
	// Gemini 3.5 Transcribe Live has no profanity filter, no denoiser and
	// no per-phrase boost weight -- so its sections below are mutually
	// exclusive with Google's, not additive to them.
	gemini_mode: 'VERBATIM',
	gemini_auto_language: false,
	gemini_custom_vocabulary: [],
	gemini_end_of_speech_sensitivity: 'HIGH',
	// 'off' transcribes everything instead of only what Gemini's own speech
	// detection hears as speech -- under crowd noise that detection missed
	// most commentary (53 words vs 384 on the same 90 s). End-of-speech
	// sensitivity applies to 'auto' only, so the panel shows it only then.
	// Mirrors JobConfig.gemini_speech_detection (default 'off').
	gemini_speech_detection: 'off',
	gemini_forward_interims: true,
	speechmatics_diarize: true,
	speechmatics_max_speakers: 6,
	speechmatics_additional_vocab: [],
	speechmatics_enable_audio_events: false,
	speechmatics_audio_event_types: [],
	// Engine-agnostic -- shown regardless of transcription_provider.
	min_display_seconds: 1.5,
	speaker_change_indicator: '>>',
	// Seconds without a source before the backend pauses this channel by
	// itself (0 = never) -- shown in minutes below, since the useful range
	// is tens of minutes. Live-editable: it is policy the supervisor reads
	// on its next tick, not something any running process was launched
	// with (see control_service.py's POLICY_LIVE_FIELDS).
	idle_pause_seconds: 0,
	video_backoff_enabled: false,
	video_backoff_idle_seconds: 60,
	video_backoff_initial_retry_seconds: 30,
	video_backoff_max_retry_seconds: 900,
};

// Must match control_service.py's STT_ONLY_HOT_SWAP_FIELDS +
// INJECTOR_LIVE_UPDATABLE_FIELDS exactly. Every field this form exposes
// now has a live-update path (Tier 0: transcription_provider,
// speechmatics_max_delay, and every Google/Speechmatics Advanced setting
// below -- all already in STT_ONLY_HOT_SWAP_FIELDS; Tier 1: all_caps,
// start_row in pop-on mode, min_display_seconds, speaker_change_indicator;
// Tier 2: cc1_lang/cc3_lang/primary_caption_channel/caption_mode/
// roll_up_rows, and start_row in roll-up mode, plus disable_708 -- see
// caption_injector.py's _configure_captioning for the
// structural-field reconfigure mechanism), EXCEPT the fields with no such
// path on the server. Membership is not decoration: fieldLockedWhileLive
// below disables every field missing from this set while captions are
// live, so this set is what keeps a reprovision-class control out of an
// on-air click and keeps its value out of a live save's diff. Kept as an
// explicit set (not just "always editable") so a future field added here
// without a live-update path fails safe (disabled, not
// clickable-then-rejected) instead of silently assuming one exists.
// google_batch_delay_seconds/google_batch_chunk_seconds/
// google_batch_safety_margin_seconds are deliberately NOT in this set --
// changing them (or switching transcription_provider to/from
// "google_batch_diarized") while live has no hot-swap path on the backend
// (it would need to insert/remove a whole extra process into the video
// pipeline, not just restart the STT process -- see
// control_service.py's own STT_ONLY_HOT_SWAP_FIELDS comment).
// local_diarize/local_diarize_endpoint are the same class of omission for
// the same reason -- see their own comment in DEFAULT_SETTINGS above.
// disable_608 is the newest member of that class and the one to be most
// careful with: its twin, disable_708, IS live-editable, and the two sit
// side by side in the same section of the form -- see the pair's own
// comment in DEFAULT_SETTINGS above before "fixing" the asymmetry.
const LIVE_EDITABLE_FIELDS = new Set([
	'transcription_provider', 'cc1_lang', 'cc3_lang', 'caption_tracks', 'primary_caption_channel', 'caption_mode', 'roll_up_rows', 'start_row', 'all_caps',
	'speechmatics_max_delay',
	'google_boost_phrases', 'google_boost_value', 'google_profanity_filter', 'google_enable_vad',
	'google_enable_denoise', 'google_denoise_snr_threshold',
	'gemini_mode', 'gemini_auto_language', 'gemini_custom_vocabulary', 'gemini_end_of_speech_sensitivity',
	'gemini_speech_detection',
	'gemini_silence_duration_ms', 'gemini_forward_interims',
	'speechmatics_diarize', 'speechmatics_max_speakers', 'speechmatics_additional_vocab',
	'speechmatics_enable_audio_events', 'speechmatics_audio_event_types',
	'min_display_seconds', 'speaker_change_indicator',
	'idle_pause_seconds',
	// The 708 gate only -- the 608 half of the format pair is
	// reprovision-class (see DEFAULT_SETTINGS above), so it stays absent
	// and locked while live, exactly like webvtt_captions_enabled.
	'disable_708',
	'video_backoff_enabled', 'video_backoff_idle_seconds',
	'video_backoff_initial_retry_seconds', 'video_backoff_max_retry_seconds',
]);

// The subset of LIVE_EDITABLE_FIELDS whose live-update path is a restart
// of the STT process -- control_service.py's own STT_ONLY_HOT_SWAP_FIELDS,
// which hot_swap_stt calls "a few seconds' caption gap". Saving one of
// these while live is an on-air action, so the panel names the cost before
// the click (see the warning above the Save button) instead of describing
// the same save as "changes apply immediately".
const STT_RESTART_FIELDS = new Set([
	'transcription_provider', 'cc1_lang',
	'google_boost_phrases', 'google_boost_value', 'google_profanity_filter', 'google_enable_vad',
	'google_enable_denoise', 'google_denoise_snr_threshold',
	'gemini_mode', 'gemini_auto_language', 'gemini_custom_vocabulary', 'gemini_end_of_speech_sensitivity',
	'gemini_speech_detection',
	'gemini_silence_duration_ms', 'gemini_forward_interims',
	'speechmatics_diarize', 'speechmatics_max_speakers', 'speechmatics_additional_vocab',
	'speechmatics_max_delay', 'speechmatics_enable_audio_events', 'speechmatics_audio_event_types',
]);

// Vendor names, deliberately not translated (they are brand names), kept
// here rather than only inside their MenuItems so the closed field can
// render the name alone while the open menu renders name + note.
const TRANSCRIPTION_PROVIDER_NAMES = {
	deepgram: 'Deepgram',
	google: 'Google Cloud Speech-to-Text',
	gemini: 'Gemini 3.5 Transcribe',
	speechmatics: 'Speechmatics',
	// Shortened from "Google Cloud Speech-to-Text (Diarized)" -- that label
	// truncates unreadably in the closed Select at this panel's real narrow
	// width (confirmed live), the same defect class as an earlier
	// "English sp..." truncation bug in this same panel.
	google_batch_diarized: 'Google (Diarized)',
	parakeet_local: 'Parakeet (local)',
};

// Build-time opt-in, like the script-following panel: the local Parakeet
// provider only runs on a host that has the model server, so it stays out
// of the menu unless this build is for such a deployment. A channel that is
// already on it keeps the entry so the Select never shows a blank value.
const parakeetLocalOffered = (current) =>
	process.env.REACT_APP_PARAKEET_LOCAL_ENABLED === 'true' || current === 'parakeet_local';

// The providers the self-hosted diarizer may be used with, exactly. This is
// the panel's mirror of restreamer_captions.PROVIDERS_FOR_LOCAL_DIARIZATION,
// which derives the same two names on the server as "supported provider AND
// NOT PROVIDERS_WITH_NATIVE_DIARIZATION" -- so a provider added there has to
// be classified on purpose before it lands here, and this list is a copy of
// that policy, never a second one. The refusal itself is the server's:
// t_captions_enable runs validate_local_diarization against the merged
// config and answers any other pairing with an HTTP 400, which is why the
// controls below are hidden rather than merely discouraged (an API caller,
// or a config written by an older UI, gets the same refusal). The rule in
// one line, as the section's own help text puts it: a self-hosted diarizer
// is only used with providers that have no diarization of their own.
//   google  -- Chirp 3 returns no diarization data in streaming mode, ever.
//   gemini  -- Gemini 3.5 Transcribe Live returns no speaker labels; Google
//              documents diarization on the non-streaming model only.
const LOCAL_DIARIZATION_PROVIDERS = ['google', 'gemini'];

// Every numeric field in the form, with the range its own inputProps
// already declare. Two jobs:
//
//  - typing. These are controlled inputs whose handlers used to ignore an
//    unparseable value, so backspacing a field to empty snapped it back to
//    the old value and it could not be cleared to retype. The operator's
//    keystrokes now live in a local string draft (see $numberDrafts) and
//    $settings only ever holds a real number.
//  - range. inputProps min/max constrain the spinner arrows, not typing,
//    so a row position of 99 or a Speechmatics latency of 9.9 (outside the
//    vendor's documented 0.7-4.0) used to post straight to a running
//    pipeline. Values are clamped on blur and again in saveSettings.
//
// scale is the field's display unit per stored unit: idle_pause_seconds is
// seconds on the wire but minutes in the form, since "600" next to every
// other control here reads as a typo.
const NUMBER_FIELDS = {
	speechmatics_max_delay: { min: 0.7, max: 4.0 },
	start_row: { min: 1, max: 15, integer: true },
	google_boost_value: { min: 0, max: 20 },
	google_denoise_snr_threshold: { min: 0 },
	google_batch_delay_seconds: { min: 0 },
	google_batch_chunk_seconds: { min: 1, max: 55 },
	google_batch_safety_margin_seconds: { min: 0 },
	video_delay_seconds: { min: 0, max: 60 },
	speechmatics_max_speakers: { min: 1, max: 100, integer: true },
	min_display_seconds: { min: 0, max: 10 },
	idle_pause_seconds: { min: 0, scale: 60 },
	video_backoff_idle_seconds: { min: 1, max: 60 },
	video_backoff_initial_retry_seconds: { min: 5, max: 3600, integer: true },
	video_backoff_max_retry_seconds: { min: 5, max: 1440, integer: true, scale: 60 },
};

// The stored value as the field displays it (minutes for idle_pause_seconds).
function numberFieldDisplay(key, value) {
	const scale = NUMBER_FIELDS[key].scale || 1;
	return String(value / scale);
}

// A typed string as the stored value, or null when there is no number in
// it at all (mid-typing "" or "-"), which callers leave alone rather than
// writing NaN into $settings.
function parseNumberField(key, raw) {
	const spec = NUMBER_FIELDS[key];
	const parsed = spec.integer ? parseInt(raw, 10) : parseFloat(raw);
	if (Number.isNaN(parsed)) {
		return null;
	}
	return spec.scale ? Math.round(parsed * spec.scale) : parsed;
}

function clampNumberField(key, value) {
	const spec = NUMBER_FIELDS[key];
	const scale = spec.scale || 1;
	const min = spec.min === undefined ? -Infinity : spec.min * scale;
	const max = spec.max === undefined ? Infinity : spec.max * scale;
	return Math.min(Math.max(value, min), max);
}

// Last line of defence before the request: a value can reach $settings
// unclamped (the operator typed it and hit Save without leaving the field),
// and nothing on the backend range-checks these.
function clampNumberSettings(settings) {
	const clamped = { ...settings };
	for (const key of Object.keys(NUMBER_FIELDS)) {
		if (typeof clamped[key] === 'number') {
			clamped[key] = clampNumberField(key, clamped[key]);
		}
	}
	return clamped;
}

export default function Captions(props) {
	const classes = useStyles();
	const { i18n } = useLingui();
	const [$status, setStatus] = React.useState({ state: 'off' });
	// Tracks our own most recent order (independent of $status.state) so
	// the Switch can go into its disabled "in flight" look immediately on
	// click, the same optimistic-then-reconciled pattern Egress.js uses
	// via its own $order/props.order pair. Stores WHICH direction ('enable'/
	// 'disable'), not just a boolean, so stateLabel can show a message that
	// matches the actual deliberate action in flight (see stateLabel's own
	// comment) instead of trusting $status's possibly-transient value for
	// that window; null means no toggle is currently in flight.
	const [$pendingAction, setPendingAction] = React.useState(null);
	const $pending = $pendingAction !== null;

	const [$settingsOpen, setSettingsOpen] = React.useState(false);
	const [$settings, setSettings] = React.useState(DEFAULT_SETTINGS);
	// What's actually been saved/confirmed, as of the last load or save --
	// diffed against $settings on save (see saveSettings) so a live edit
	// only submits fields the operator actually touched this session, not
	// every LIVE_EDITABLE_FIELDS entry regardless of whether its value
	// moved. Distinguishing "changed" from "merely present" matters a lot
	// here: cc1_lang/transcription_provider being present at all (even
	// unchanged) triggers a real stt hot-swap (a few seconds' caption
	// gap) -- confirmed live, this exact thing happening on every single
	// save while live (e.g. just toggling ALL CAPS) was caught during
	// end-to-end verification, not left as a theoretical concern.
	const [$settingsBaseline, setSettingsBaseline] = React.useState(DEFAULT_SETTINGS);
	const [$settingsLoading, setSettingsLoading] = React.useState(false);
	const [$settingsSaving, setSettingsSaving] = React.useState(false);
	// Whether a GET has ever actually populated this form. Everything the
	// form shows before that is DEFAULT_SETTINGS -- indistinguishable from
	// real data, and saving it would write factory defaults over a channel
	// configured for Speechmatics, Spanish on CC3, roll-up 3. saveSettings
	// refuses while this is false, independently of the error rendering
	// below, so the destroy path stays closed even if the form is ever
	// shown again on a failed load.
	const [$settingsLoaded, setSettingsLoaded] = React.useState(false);
	// null, or {scope: 'load'|'save', detail}. The two failures are not the
	// same event: a failed load means "these are not your channel's
	// settings" and must not invite a Save, while a failed save is about
	// the request the operator just made. detail is the API's own message
	// (control_service.py writes a genuinely useful one -- "turn captions
	// off before changing X"), which used to be discarded for "Try again",
	// advice that could only fail identically forever.
	const [$settingsError, setSettingsError] = React.useState(null);
	// {time, tracksChanged} of the last successful save this session. The
	// panel deliberately stays open for another edit, so without this the
	// only feedback a save produced was the "Unsaved changes" line
	// disappearing -- a removal, at the bottom of a very long card. The
	// timestamp is worth more than a checkmark to someone reconciling a
	// log afterwards.
	const [$lastSaved, setLastSaved] = React.useState(null);
	// Numeric fields as the operator is typing them, keyed by settings
	// field -- see NUMBER_FIELDS. Present only while a field is being
	// edited; $settings still carries the parsed number throughout.
	const [$numberDrafts, setNumberDrafts] = React.useState({});
	// Set when the operator moves the switch from on to off while captions
	// are actually live. Turning captions ON stays instant -- it is the
	// safe direction -- but the off direction stops captions on a
	// broadcast with no undo, so it gets an inline confirmation strip
	// rather than firing DisableCaptions straight from the click.
	const [$confirmDisable, setConfirmDisable] = React.useState(false);
	// SIGNUP_PLAN.md: set when EnableCaptions comes back 402 (free trial
	// exhausted, no payment method on file yet -- see control_service.py's
	// trial gate). Deliberately separate from $status.state: GetCaptionsStatus
	// has no concept of billing at all, so this can't be folded into the
	// same enum without control_service.py inventing a status value real
	// core-managed captioning state doesn't have.
	const [$billingBlocked, setBillingBlocked] = React.useState(false);
	const [$upgrading, setUpgrading] = React.useState(false);
	// Set whenever resolveLangCode had to fall back to an arbitrary language
	// (changed=true, not just a same-language code reformat) -- see
	// resolveLangCode's own comment. Cleared the moment the operator picks
	// a language themselves (updateSetting('cc1_lang')), not on a timer:
	// this is a "you need to look at this before saving" flag, not a
	// transient toast.
	const [$languageResetWarning, setLanguageResetWarning] = React.useState(null);

	const refresh = React.useCallback(async () => {
		try {
			const status = await AirchainAPI.GetCaptionsStatus(props.channelid, props.restreamer.GetAccessToken());
			setStatus(status);
		} catch (e) {
			setStatus({ state: 'error', detail: 'unreachable' });
		}
	}, [props.channelid, props.restreamer]);

	// Paused (delay=null) while a toggle is in flight -- see stateLabel's
	// comment: this is what actually closes the race, not just the label
	// override. Without this, the poll fires on its own 1s cadence
	// regardless of handleSwitch, so it can land a GET mid-flight during
	// core's blocking process stop/start and reconcile $status right back
	// to a transient/wrong value the instant this component re-renders.
	useInterval(refresh, $pending ? null : 1000);

	React.useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSwitch = (event) => {
		const enabling = event.target.checked;
		if (!enabling && $status.state === 'live') {
			setConfirmDisable(true);
			return;
		}
		applyToggle(enabling);
	};

	const applyToggle = async (enabling) => {
		setConfirmDisable(false);
		setPendingAction(enabling ? 'enable' : 'disable');
		if (enabling) {
			setBillingBlocked(false);
		}
		try {
			if (enabling) {
				const status = await AirchainAPI.EnableCaptions(props.channelid, props.restreamer.GetAccessToken());
				setStatus(status);
			} else {
				const status = await AirchainAPI.DisableCaptions(props.channelid, props.restreamer.GetAccessToken());
				setStatus(status);
			}
		} catch (e) {
			if (e.status === 402) {
				// Trial exhausted -- a real, expected outcome for a free
				// signup, not a system failure, so this gets its own
				// upgrade prompt (below) instead of the generic
				// "stopped unexpectedly" error label.
				setBillingBlocked(true);
			} else {
				setStatus({ state: 'error', detail: 'request-failed' });
			}
		} finally {
			setPendingAction(null);
		}
	};

	const handleUpgrade = async () => {
		setUpgrading(true);
		try {
			const result = await AirchainAPI.CreateBillingCheckout(props.restreamer.GetAccessToken());
			window.location.href = result.checkout_url;
			// Deliberately no setUpgrading(false) on the success path -- the
			// browser is navigating away to Stripe's own hosted page, so
			// this component's state ceases to matter; leaving the button
			// showing its loading state avoids a flash of "clickable again"
			// right before the redirect actually happens.
		} catch (e) {
			setUpgrading(false);
		}
	};

	const toggleSettings = () => {
		if ($settingsOpen) {
			setSettingsOpen(false);
			return;
		}

		setSettingsOpen(true);
		loadSettings();
	};

	const loadSettings = async () => {
		setSettingsLoading(true);
		setSettingsError(null);
		try {
			const saved = await AirchainAPI.GetCaptionsSettings(props.channelid, props.restreamer.GetAccessToken());
			const loaded = { ...DEFAULT_SETTINGS, ...saved };
			// Previously-saved data can predate the language pickers being
			// constrained to each provider's real supported-language list
			// (e.g. a free-text "es-ES" saved for cc3_lang before it became
			// Google Translate's own bare "es" codes) -- normalize on load
			// so the dropdowns show a real selection, not a blank field.
			let resetWarning = null;
			const cc1Resolved = resolveLangCode(
				loaded.cc1_lang,
				CC1_LANGUAGES_BY_PROVIDER[loaded.transcription_provider].map((lang) => lang.code),
			);
			loaded.cc1_lang = cc1Resolved.code;
			if (cc1Resolved.changed) {
				const label = CC1_LANGUAGES_BY_PROVIDER[loaded.transcription_provider].find((lang) => lang.code === cc1Resolved.code)?.label;
				resetWarning = { field: 'cc1_lang', label };
			}
			if (loaded.cc3_lang) {
				const cc3Resolved = resolveLangCode(loaded.cc3_lang, CC3_LANGUAGES.map((lang) => lang.code));
				loaded.cc3_lang = cc3Resolved.code;
				if (cc3Resolved.changed) {
					const label = CC3_LANGUAGES.find((lang) => lang.code === cc3Resolved.code)?.label;
					resetWarning = resetWarning || { field: 'cc3_lang', label };
				}
			}
			setLanguageResetWarning(resetWarning);
			setSettings(loaded);
			setSettingsBaseline(loaded);
			setNumberDrafts({});
			setSettingsLoaded(true);
		} catch (e) {
			setSettingsError({ scope: 'load', detail: e.detail });
		} finally {
			setSettingsLoading(false);
		}
	};

	const updateSetting = (key) => (event) => {
		setSettings({ ...$settings, [key]: event.target.value });
		// A manual pick on either language field resolves whichever warning
		// resulted from the automatic fallback -- the operator has now
		// looked at and confirmed a real choice, whether or not it's the
		// same field the warning was about.
		if (key === 'cc1_lang' || key === 'cc3_lang') {
			setLanguageResetWarning(null);
		}
	};

	const handleSecondLanguageToggle = (event) => {
		setSettings({ ...$settings, cc3_lang: event.target.checked ? 'es' : '' });
	};

	// Autocomplete's onChange reports (event, newValue) -- the whole
	// {code, label} option object, or null when cleared -- not a plain
	// event.target.value like updateSetting's other fields, so cc1_lang/
	// cc3_lang need their own handlers rather than reusing that one.
	const handleCc1LangChange = (event, newValue) => {
		setSettings({ ...$settings, cc1_lang: newValue ? newValue.code : '' });
		setLanguageResetWarning(null);
	};

	const handleCc3LangChange = (event, newValue) => {
		setSettings({ ...$settings, cc3_lang: newValue ? newValue.code : '' });
		setLanguageResetWarning(null);
	};

	const handleProviderChange = (event) => {
		// Each provider supports a different set of real-time recognition
		// languages (see caption-languages.js) -- switching providers can
		// leave cc1_lang pointing at a code the new provider doesn't
		// support at all (e.g. Speechmatics' bare "es" vs. Google's
		// region-qualified "es-ES"), so remap it via the same
		// exact-then-base-language fallback used when loading previously-
		// saved settings, rather than silently carrying forward an invalid
		// code. transcription_provider/cc1_lang are BOTH live-editable (see
		// LIVE_EDITABLE_FIELDS below), so this can happen while captions are
		// actually airing -- an unannounced language substitution here was
		// a real gap an isolated design review caught, not a hypothetical:
		// changed=true surfaces $languageResetWarning instead of silently
		// applying an arbitrary language nobody chose.
		const provider = event.target.value;
		const validCodes = CC1_LANGUAGES_BY_PROVIDER[provider].map((lang) => lang.code);
		const resolved = resolveLangCode($settings.cc1_lang, validCodes);
		// Card #54: the same shape of gap as the language remap above, on the
		// other side of a gate. The self-hosted diarizer's controls exist only
		// for the providers that can run it (see
		// LOCAL_DIARIZATION_PROVIDERS), and the server refuses the pairing
		// outright -- t_captions_enable runs validate_local_diarization and
		// answers anything else with an HTTP 400 (the settings PUT will store
		// it, but the enable that follows never will). Leaving a saved true on
		// a control this picker has just taken off the screen would be a
		// channel nobody can switch back on from this panel, so switching to
		// a provider that labels its own speakers clears the flag here. The
		// endpoint deliberately stays: it is only meaningful while the flag is
		// on, and keeping it means switching back does not lose the address.
		const localDiarizationUsable = LOCAL_DIARIZATION_PROVIDERS.includes(provider);
		setSettings({
			...$settings,
			transcription_provider: provider,
			cc1_lang: resolved.code,
			...(localDiarizationUsable ? {} : { local_diarize: false }),
		});
		if (resolved.changed) {
			const label = CC1_LANGUAGES_BY_PROVIDER[provider].find((lang) => lang.code === resolved.code)?.label;
			setLanguageResetWarning({ field: 'cc1_lang', label });
		} else {
			setLanguageResetWarning(null);
		}
	};

	const handleAllCapsToggle = (event) => {
		setSettings({ ...$settings, all_caps: event.target.checked });
	};

	const handleCheckboxSettingToggle = (key) => (event) => {
		setSettings({ ...$settings, [key]: event.target.checked });
	};

	// The two CEA format gates (see the disable_608/disable_708 comment in
	// DEFAULT_SETTINGS above). The control is positive and the stored flag is
	// not, so this inverts -- exactly once, here, and once in each checked=
	// expression below. Nothing else in the panel knows the control is
	// positive: $settings, the baseline diff and the request body all carry
	// the server's own disable_* flag, which is what keeps a value persisted
	// by an API caller meaning the same thing when this form loads it.
	//
	// The locked-field refusal is defence in depth, not a duplicate of the
	// disabled prop: the prop is what an operator meets (and is enough on its
	// own -- a browser dispatches no events at all at a disabled control), but
	// this is the half that makes the panel's own claim true in the model, not
	// only in the DOM. A change that reached $settings anyway (a synthetic
	// event, a future caller rendering this control without the prop) would
	// become part of the next live save's diff, and control_service.py answers
	// a live save carrying a reprovision-class field with a 409 -- which would
	// lose the operator's real edits along with it. Refusing here keeps the
	// flag out of that diff no matter how the change arrived.
	const handleCaptionFormatToggle = (key) => (event) => {
		if (fieldLockedWhileLive(key)) {
			return;
		}
		setSettings({ ...$settings, [key]: !event.target.checked });
	};

	// value/onChange/onBlur for every numeric field (see NUMBER_FIELDS),
	// reused across all ten instead of one bespoke handler per field.
	//
	// While the field has focus the operator's own string is what it
	// shows, so it can be cleared to empty and retyped, and an
	// intermediate "2." survives long enough to become "2.5" -- neither
	// was possible when the handler simply ignored anything unparseable
	// and React re-rendered the previous number straight back over it.
	// $settings still only ever holds a number: a parseable draft commits
	// on every keystroke (so the Save button and the unsaved-changes line
	// react immediately), and blur clamps it to the field's declared
	// range.
	const numberFieldProps = (key) => ({
		value: key in $numberDrafts ? $numberDrafts[key] : numberFieldDisplay(key, $settings[key]),
		onChange: (event) => {
			const raw = event.target.value;
			setNumberDrafts({ ...$numberDrafts, [key]: raw });
			const parsed = parseNumberField(key, raw);
			if (parsed !== null) {
				setSettings({ ...$settings, [key]: parsed });
			}
		},
		onBlur: () => {
			if (!(key in $numberDrafts)) {
				return;
			}
			const parsed = parseNumberField(key, $numberDrafts[key]);
			const { [key]: draft, ...remaining } = $numberDrafts;
			setNumberDrafts(remaining);
			// An empty or unparseable draft falls back to the last good
			// value rather than to zero, which for e.g. min_display_seconds
			// would silently change a real setting.
			if (parsed !== null) {
				setSettings({ ...$settings, [key]: clampNumberField(key, parsed) });
			}
		},
	});

	// Autocomplete's multiple+freeSolo onChange reports (event, newValue)
	// as the full array of chip strings, same shape needed for
	// google_boost_phrases/speechmatics_additional_vocab directly -- no
	// per-item parsing needed the way cc1_lang/cc3_lang's option-object
	// handlers require.
	const handleTagListSettingChange = (key) => (event, newValue) => {
		setSettings({ ...$settings, [key]: newValue });
	};

	// speechmatics_audio_event_types' own display labels -- computed here
	// (not a top-level constant) since the t`` macro needs the component's
	// own i18n instance to resolve to the active locale, same reason
	// speechmatics_additional_vocab's placeholder text above is computed
	// inline rather than declared statically.
	const speechmaticsAudioEventTypeLabel = (type) => {
		switch (type) {
			case 'laughter':
				return i18n._(t`Laughter`);
			case 'applause':
				return i18n._(t`Applause`);
			case 'music':
				return i18n._(t`Music`);
			default:
				return type;
		}
	};

	// One provider option: the vendor's name, one line of what actually
	// differs between them, and a mark on the value in use. The menu was
	// five SKUs with no basis for choosing between them, and the current
	// one was marked only by a background shift too slight to survive a
	// glance. Language counts come from caption-languages.js's own
	// per-provider lists.
	const providerOption = (provider, note) => (
		<Stack className={classes.providerOption} direction="row" alignItems="center" spacing={1}>
			<Stack direction="column" spacing={0}>
				<Typography className="provider-name">{TRANSCRIPTION_PROVIDER_NAMES[provider]}</Typography>
				{note && <Typography className="provider-note">{note}</Typography>}
			</Stack>
			{$settings.transcription_provider === provider && <CheckIcon className="provider-check" fontSize="small" />}
		</Stack>
	);

	const saveSettings = async () => {
		// A form that never loaded is a form full of DEFAULT_SETTINGS, and
		// saving it would overwrite this channel's real configuration with
		// factory defaults (the diff path below only applies while live --
		// while off, every field goes up). Nothing in the UI offers this
		// today, since a failed load renders its own error instead of the
		// form; the guard is here so it stays closed regardless.
		if (!$settingsLoaded) {
			return;
		}

		// Whatever the operator last typed, held to the range each field
		// declares -- a value can reach $settings unclamped by hitting
		// Save without leaving the field first.
		const settingsToSave = clampNumberSettings($settings);

		// While live, submit only fields that actually changed since the
		// panel was loaded (diffed against $settingsBaseline), not every
		// LIVE_EDITABLE_FIELDS entry regardless of value -- the backend's
		// model_fields_set is keyed on which JSON fields are PRESENT in the
		// request body, not which values differ, so including cc1_lang/
		// transcription_provider at their current, unchanged value would
		// still trigger a real stt hot-swap (a genuine caption gap) on
		// every single live save, e.g. just for toggling ALL CAPS.
		// Confirmed live during end-to-end verification -- this isn't a
		// theoretical concern. While off/error, submit everything as
		// before (no live-update cost to avoid, and a fresh enable should
		// see the complete picture regardless of what changed just now).
		const changedFields = checked
			? Object.fromEntries(Object.entries(settingsToSave).filter(([key, value]) => value !== $settingsBaseline[key]))
			: settingsToSave;

		if (checked && Object.keys(changedFields).length === 0) {
			// Nothing actually changed -- skip the round-trip entirely
			// rather than firing a no-op save (and, worse, an unnecessary
			// hot-swap if a live-editable-but-untouched field somehow slipped
			// into the payload). The button is disabled in this state now,
			// so this is no longer the click's only visible outcome; it
			// stays as the guard it always was. Panel stays open, same as a
			// real live save would.
			return;
		}

		// Whether the operator changed which languages get their own
		// caption track: the player has to be reloaded to pick those up,
		// which is a post-save instruction and belongs in the save
		// confirmation rather than in the field's own pre-save help text.
		const tracksChanged = JSON.stringify($settings.caption_tracks) !== JSON.stringify($settingsBaseline.caption_tracks);

		setSettingsSaving(true);
		setSettingsError(null);
		try {
			const saved = await AirchainAPI.SaveCaptionsSettings(props.channelid, props.restreamer.GetAccessToken(), changedFields);
			const merged = { ...DEFAULT_SETTINGS, ...saved };
			setSettings(merged);
			setSettingsBaseline(merged);
			setNumberDrafts({});
			setLastSaved({
				time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
				tracksChanged,
			});
			// Only auto-close on the "configure before turning on" flow --
			// a live edit is the "iteratively tweak while watching captions"
			// flow (the whole point of LIVE_EDITABLE_FIELDS), so keep the
			// panel open for another adjustment instead of making the
			// operator re-open the gear after every single change.
			if (!checked) {
				setSettingsOpen(false);
			}
		} catch (e) {
			// The server's own words, not "Try again": a 409 names the
			// exact field that needs captions turned off first, and a 502
			// says the injector is unreachable -- an infrastructure fault
			// nobody should be invited to retry blindly.
			setSettingsError({ scope: 'save', detail: e.detail });
		} finally {
			setSettingsSaving(false);
		}
	};

	// The symmetric exit to Save. Closing the panel already discards an
	// edit, but silently and only as a side effect of leaving -- this says
	// out loud that there is a way back, and puts the numeric drafts back
	// with everything else.
	const discardChanges = () => {
		setSettings($settingsBaseline);
		setNumberDrafts({});
		setLanguageResetWarning(null);
		setSettingsError(null);
	};

	// 'idle' counts as on: the operator did not turn this channel off, the
	// supervisor paused it for a missing source and will want it back (see
	// stateLabel). Showing the toggle off would invite a click that
	// "turns on" something already on.
	const checked = $status.state === 'starting' || $status.state === 'live' || $status.state === 'idle';
	// The gear itself is only locked during the brief "starting" window
	// (processes are still being created -- see t_captions_put_settings'
	// own "captions are still starting" 409) and not on error (the switch
	// already allows retrying straight from error, see handleSwitch/
	// PRODUCT.md's "make the safe action obvious during an incident", and
	// error looks identical to off here -- checked is false for both).
	// While actually live, the gear opens fine; individual fields below
	// are disabled per LIVE_EDITABLE_FIELDS instead of blocking the whole
	// panel, since some of them (provider, ALL CAPS) really can be changed
	// live now.
	const settingsDisabled = $status.state === 'starting';
	const fieldLockedWhileLive = (field) => $status.state === 'live' && !LIVE_EDITABLE_FIELDS.has(field);
	// The one operational fact a greyed-out control cannot state for itself:
	// this channel cannot take the change while captions are running, so the
	// control is disabled and the route through is off -> change -> on. It
	// is attached as a title= to every control the lock disables -- the
	// same workaround the CEA-608 control spells out in its own helper text
	// (see the format pair below), and the one google_isolate_vocals and
	// local_diarize already put in theirs. undefined, not '', when the field
	// is editable: an empty title= would still render a tooltip.
	//
	// One exception, and it is why some call sites read differently: a
	// misc/Select forwards only the props it declares and drops the rest, so
	// a title= handed to a Select never reaches the DOM -- for those six
	// controls the same title sits on the enclosing Grid cell instead, and
	// covers the field's own label with it. A disabled Checkbox cannot be
	// hovered either, so the label wrapping it carries the title too.
	const lockedFieldTitle = (field) =>
		fieldLockedWhileLive(field) ? i18n._(t`Switch captions off, change this, then switch them back on.`) : undefined;
	// transcription_provider itself IS in LIVE_EDITABLE_FIELDS (the other
	// three providers hot-swap fine live) -- but switching to/from
	// "google_batch_diarized" specifically has no hot-swap path (see
	// LIVE_EDITABLE_FIELDS' own comment) and the backend 409s on it while
	// live. $settingsBaseline, not $settings, is what's actually running
	// right now -- checked against that, not against an unsaved draft
	// value, so this can't disable the wrong option mid-edit.
	const currentlyBatchDiarized = $settingsBaseline.transcription_provider === 'google_batch_diarized';
	// Gemini's auto-detect takes the language choice out of the operator's
	// hands (empty language_codes on the wire -- see gemini_stt_client.py),
	// so the picker is disabled while it's on rather than left looking like
	// it still controls recognition. Read off $settings, not
	// $settingsBaseline: this one reflects the draft being edited, since the
	// control it disables is edited in the same draft.
	const geminiAutoLanguage = $settings.transcription_provider === 'gemini' && !!$settings.gemini_auto_language;
	// Purely derived, not its own state -- closing the gear (toggleSettings)
	// discards $settings without warning today; this at least makes that
	// loss visible beforehand instead of silent, the cheapest version of
	// the fix without adding a confirm-on-close interaction.
	const hasUnsavedChanges = JSON.stringify($settings) !== JSON.stringify($settingsBaseline);
	// Saving one of these while live restarts recognition -- see
	// STT_RESTART_FIELDS. Derived from the same diff saveSettings submits,
	// so the warning appears exactly when the request would carry one.
	const restartsRecognitionOnSave =
		$status.state === 'live' && Object.keys($settings).some((key) => STT_RESTART_FIELDS.has(key) && $settings[key] !== $settingsBaseline[key]);
	// null means "no preview at all" -- not live, or live but the status
	// carried no on_screen (an injector that didn't answer control_service's
	// own fetch this tick, see its _caption_preview). That is deliberately
	// NOT the same as the empty array, which is a reachable injector
	// reporting a blank screen and gets the "waiting" line below.
	const $onScreen = $status.state === 'live' && $status.on_screen ? onScreenChannels($status.on_screen) : null;
	// null when the response carries no speakers at all -- an injector older
	// than the field, or any non-live state (control_service.py attaches the
	// preview and this with it, see its t_captions_status). Deliberately
	// distinct from the empty array, which is a live injector reporting no
	// channel to label and renders nothing too, but there is nothing true to
	// say about it either.
	const $speakers = speakerChannels($status.speakers);
	const $statusPresentation = statusPresentation($status.state, $pendingAction, $status.detail);
	const StatusIcon = $statusPresentation.Icon;

	// What this channel puts out, on the surface instead of behind the gear
	// -- the line a venue, a buyer or a support call can be pointed at, and
	// the one an operator can read back to prove what is being delivered.
	// Derived from the same three settings fields the Delivered formats
	// controls write (see deliveredFormats), so it can never disagree with
	// them, and gated on $settingsLoaded ON PURPOSE: until this channel's
	// own settings have actually been read, $settings is DEFAULT_SETTINGS,
	// and a line claiming "Emitting: CEA-608 · CEA-708" off those defaults
	// would be a statement about a channel nobody has looked at yet --
	// exactly the thing this line exists to stop. No API call of its own:
	// it appears the moment the settings this panel already loads anywhere
	// (opening the gear, or a save) are known.
	const $deliveredFormats = $settingsLoaded ? deliveredFormats($settings) : null;
	const $emittedFormats = $deliveredFormats ? $deliveredFormats.filter((format) => format.on).map((format) => format.label) : [];
	const $suppressedFormats = $deliveredFormats ? $deliveredFormats.filter((format) => !format.on).map((format) => format.label) : [];
	// Two clauses rather than one list, because a format that is switched
	// off has to be named as off rather than dropped from the sentence: the
	// emitter list alone would let a reader infer a format the channel is
	// not sending.
	const $formatState = $deliveredFormats
		? [
				$emittedFormats.length > 0 ? i18n._(t`Emitting: ${$emittedFormats.join(' · ')}`) : i18n._(t`Not emitting any caption format`),
				$suppressedFormats.length > 0 ? i18n._(t`${$suppressedFormats.join(' · ')} off`) : null,
		  ]
				.filter(Boolean)
				.join(' — ')
		: null;

	// The heartbeat (see lastCaption): how long ago the newest caption
	// actually appeared, and what it said, so a room that has gone quiet and
	// a source that has died stop reading identically. $captionsWentQuiet is
	// the one case the injector's own payload distinguishes for us, with no
	// threshold invented here: a channel only empties its screen after
	// caption_injector.py's no-data watchdog has cleared it (that watchdog
	// is the one thing that sets `displayed` back to None after a reveal),
	// so an empty screen whose age is above zero is captions that have
	// genuinely stopped arriving, while an empty screen with no reveal yet
	// (age 0.0) is simply nothing said so far.
	const $lastCaption = $onScreen !== null ? lastCaption($status.on_screen) : null;
	const $newestCaption = $lastCaption && $lastCaption.text ? $lastCaption : null;
	// Named without this file's usual $ prefix on purpose: a {placeholder}
	// in a Trans is only resolved at runtime when it is a bare identifier in
	// scope (the same reason the speaker readout below destructures
	// `current` before interpolating it). A $-prefixed name is not treated
	// as a placeholder at all -- it renders on screen with its braces intact.
	const ageLabel = $lastCaption ? formatAge($lastCaption.age) : null;
	const $captionsWentQuiet = $onScreen !== null && $onScreen.length === 0 && $lastCaption !== null && $lastCaption.age > 0;
	// The heartbeat's headline, when the injector has really measured one
	// (see captionLatency): the average caption latency over the last
	// window -- how far behind the live audio the text is. Everything above
	// is freshness; this is the only latency on the panel, it is the
	// injector's own average and not a per-tick age dressed up as one, and
	// it is quoted as measured -- sign included -- rather than rounded
	// toward zero.
	//
	// $latencyHeadline is one of 'measured' / 'keeping-up' / null, and null
	// is the fallback case: an injector too old to report the field, or one
	// that has not collected a sample yet, leaves the age line below doing
	// the job it always did. Deliberately not a third state on screen: a
	// missing measurement must not render as a measurement of zero.
	const $captionLatency = captionLatency($status.caption_latency);
	const $latencyHeadline = $captionLatency === null
		? null
		: $captionLatency.mean <= KEEPING_UP_SECONDS
			? 'keeping-up'
			: 'measured';
	// Bare identifiers, not $-prefixed, for the same reason ageLabel above is:
	// a bare name in scope is what a {placeholder} in a Trans resolves to. A
	// $-prefixed one is not treated as a placeholder at all and renders on
	// screen with its braces intact.
	const latencyMean = $captionLatency ? formatLatencyMean($captionLatency.mean) : null;
	const latencyWindow = $captionLatency ? $captionLatency.window : null;

	// The three classes of live edit, each read off the sets that actually
	// drive it rather than described from memory: a live save carrying a
	// field in STT_RESTART_FIELDS restarts stt (a few seconds without
	// captions), any other LIVE_EDITABLE_FIELDS field applies with no
	// interruption at all, and a field outside LIVE_EDITABLE_FIELDS has its
	// control disabled outright by fieldLockedWhileLive -- for those the
	// only route is off, change, on. One sentence claimed every change
	// "applies immediately" and then qualified it, which named neither the
	// fields that cost a caption gap nor the reason half the form had gone
	// grey.
	const $liveRestartFields = Object.keys($settings).filter((key) => LIVE_EDITABLE_FIELDS.has(key) && STT_RESTART_FIELDS.has(key));
	const $liveLockedFields = Object.keys($settings).filter((key) => fieldLockedWhileLive(key));

	return (
		<Paper marginBottom="0">
			<PaperHeader
				title={<Trans>Captions</Trans>}
				onEdit={toggleSettings}
				editDisabled={settingsDisabled}
				editTitle={settingsDisabled ? i18n._(t`Captions are starting — try again in a moment`) : i18n._(t`Caption settings`)}
			/>
			<Grid container spacing={1} className={classes.captionsBar}>
				{/* State, switch and preview together, and stuck to the top of
				the card: this is the block that answers "is the broadcast fine
				right now", and the form below it is long enough to scroll it
				off the screen exactly while it matters most. */}
				<Grid item xs={12} className={classes.statusSticky}>
					<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
						<Stack className="captions-left" direction="row" alignItems="flex-start" spacing={1}>
							<StatusIcon className={`captions-state-icon is-${$statusPresentation.tone}`} fontSize="small" />
							<Stack direction="column" spacing={0}>
								<Typography className="captions-name">{$statusPresentation.label}</Typography>
								{$statusPresentation.detail && <Typography className="captions-detail">{$statusPresentation.detail}</Typography>}
								{/* Selectable, not a title= tooltip: this is the string an
								operator reads down the phone to support, and a value you
								cannot copy (or reach at all on a touch device) is a value
								they transcribe by eye. It stays visibly subordinate --
								the operator's answer is the line above it. */}
								{$status.state === 'error' && $status.detail && (
									<Typography className="captions-ref">
										<Trans>Ref:</Trans> <span>{$status.detail}</span>
									</Typography>
								)}
							</Stack>
						</Stack>
						<Switch
							checked={checked}
							disabled={$pending || $status.state === 'starting'}
							onChange={handleSwitch}
							color={switchColor($status.state, $pendingAction)}
							size="small"
						/>
					</Stack>

					{/* What this channel puts out, on the surface rather than behind
					the gear: the line a venue, a buyer or a support call can be
					pointed at, and the one an operator can read back to prove what
					is going out. Purely derived (see $formatState), and in the
					warning tone when nothing at all is emitted -- a real way to
					run captions that produce no output, and the one state here
					worth stopping on. Gated on $settingsLoaded: see its own
					comment above. */}
					{$formatState && (
						<Typography className={`captions-detail${$emittedFormats.length === 0 ? ' is-warning' : ''}`}>
							{$formatState}
						</Typography>
					)}

					{$onScreen !== null && (
						<Stack className={classes.captionPreview} direction="column" spacing={0.5}>
							{/* The heartbeat: how far behind live the captions are,
							or -- with no measurement to show -- how long ago
							the injector last put text on screen. It
							deliberately does NOT repeat the words -- the
							preview below already shows them, and an operator
							watching the panel read the same sentence twice
							(reported against the first cut of this line).
							Freshness was all this line used to report, and
							operators read it as a latency; the average from
							the injector is the latency, so it leads whenever
							there is one (see $latencyHeadline), and the age
							stays as the fallback for an injector that reports
							none. Rendered only when there is something true to
							say: a measured average, or rows with an age, or an
							empty screen -- which is what separates "nothing
							said yet" (age 0.0, the channel is fine) from
							"nothing arriving for a while". The latter is
							$captionsWentQuiet -- the injector's own no-data
							watchdog has already cleared the screen -- and it
							outranks the average: a channel whose captions have
							stopped is the more urgent fact, and the two used
							to render byte-identically. */}
							{($newestCaption || $onScreen.length === 0 || $latencyHeadline) && (
								<Typography className={`captions-detail${$captionsWentQuiet ? ' is-warning' : ''}`}>
									{$captionsWentQuiet ? (
										<Trans>No captions for {ageLabel}. Check the source.</Trans>
									) : $latencyHeadline === 'keeping-up' ? (
										<Trans>Captions are keeping up with the live feed</Trans>
									) : $latencyHeadline === 'measured' ? (
										<Trans>Average caption latency {latencyMean}s over the last {latencyWindow}s</Trans>
									) : $newestCaption ? (
										<Trans>Last caption {ageLabel} ago</Trans>
									) : (
										<Trans>Waiting for speech…</Trans>
									)}
								</Typography>
							)}
							{$onScreen.map(([number, channel]) => (
								// Label line above the rows rather than beside them (the
								// marketing mock's own arrangement): this panel lives in a
								// md={4} column, and a 32-column monospace block plus a
								// label column doesn't reliably fit there.
								<Stack key={number} direction="column" spacing={0.25}>
									<Stack direction="row" alignItems="center" spacing={0.5}>
										<Typography className="caption-preview-cc">{captionChannelLabel(number)}</Typography>
										<Typography className="caption-preview-lang">{channel.lang}</Typography>
									</Stack>
									{/* Exactly as sent -- already uppercased upstream when
									ALL CAPS is on, and already wrapped to the rows the
									encoder placed. Nothing here re-wraps or re-cases. */}
									<Typography className="caption-preview-rows" component="div">
										{channel.rows.map((row, index) => (
											<div key={index}>{row}</div>
										))}
									</Typography>
								</Stack>
							))}
						</Stack>
					)}
				</Grid>

				{/* The labeller's own state, under the preview and off the same
				poll (see $speakers): who the injector is labelling right now,
				and how many times that has changed since this channel went
				live. It is the line that answers "is diarization actually
				running?" on single-speaker content, where the only other
				visible output -- the ">>" marker -- never appears because
				nobody ever changes speaker.
				Rendered only when the response carries `speakers`: an injector
				too old to report it shows nothing here, never a blank line
				that would read as "this channel has no speaker". With more than
				one caption channel the readout names each one, off the same CC
				numbers the rows above are labelled with. */}
				{$speakers && $speakers.length > 0 && (
					<Stack className={classes.captionPreview} direction="column" spacing={0.25}>
						{$speakers.map(([number, state]) => {
							// Destructured rather than read straight off `state`:
							// the message below interpolates a bare {current},
							// which lingui can resolve from the local scope in
							// both its untranslated and its translated form.
							const { current, changes = 0, labelled } = state;
							return (
								<Stack key={number} direction="row" alignItems="center" spacing={0.5}>
									{$speakers.length > 1 && (
										<Typography className="caption-preview-cc">{captionChannelLabel(number)}</Typography>
									)}
									<Typography className="captions-detail">
										{labelled && current ? (
											<Trans>
												Speaker {current} · <Plural value={changes} one="# change" other="# changes" />
											</Trans>
										) : (
											<Trans>Speaker: not labelled yet</Trans>
										)}
									</Typography>
								</Stack>
							);
						})}
					</Stack>
				)}
				{/* Enabling stays instant; only the direction that stops
				captions on air asks first, and it asks inside the card rather
				than in a modal an operator would learn to dismiss. */}
				{$confirmDisable && (
					<Grid item xs={12}>
						<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
							<Typography className="captions-detail is-warning">
								<Trans>Turn captions off on air?</Trans>
							</Typography>
							<Stack direction="row" spacing={1}>
								<Button size="small" variant="outlined" color="secondary" onClick={() => applyToggle(false)}>
									<Trans>Turn off</Trans>
								</Button>
								<Button size="small" variant="outlined" color="default" onClick={() => setConfirmDisable(false)}>
									<Trans>Keep captioning</Trans>
								</Button>
							</Stack>
						</Stack>
					</Grid>
				)}

				{$billingBlocked && (
					<Grid item xs={12} className={classes.settingsArea}>
						<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
							<Typography className="captions-detail">
								<Trans>Your free trial has ended. Add a payment method to keep captioning.</Trans>
							</Typography>
							{/* A payment prompt is something the product wants the
							operator to do -- color="secondary" is this theme's danger
							token (see button.js), which rendered it as a destructive
							action. */}
							<Button size="small" variant="outlined" color="primary" onClick={handleUpgrade} disabled={$upgrading}>
								{$upgrading ? <Trans>Redirecting…</Trans> : <Trans>Add payment method</Trans>}
							</Button>
						</Stack>
					</Grid>
				)}

				{$settingsOpen && (
					<Grid item xs={12} className={classes.settingsArea}>
						{$settingsLoading ? (
							<Stack alignItems="center" sx={{ py: 2 }}>
								<CircularProgress size={22} />
							</Stack>
						) : $settingsError !== null && $settingsError.scope === 'load' ? (
							// A form full of DEFAULT_SETTINGS is indistinguishable from a
							// form full of this channel's real settings, so a failed load
							// does not render one. The old message named the wrong
							// operation ("Couldn't save settings") and then pointed the
							// operator at the single most damaging thing available: Save,
							// which would have written those defaults over a channel
							// configured for Speechmatics, Spanish on CC3, roll-up 3.
							<Stack direction="column" spacing={1} alignItems="flex-start">
								<Typography className="captions-detail is-error">
									<Trans>Couldn't load this channel's caption settings.</Trans>
								</Typography>
								{$settingsError.detail && <Typography className="captions-detail">{$settingsError.detail}</Typography>}
								<Button size="small" variant="outlined" color="default" onClick={loadSettings}>
									<Trans>Retry</Trans>
								</Button>
							</Stack>
						) : (
							<React.Fragment>
								<Grid container spacing={2} className={classes.settingsScroll}>
									{/* Branches on the real state rather than on `checked`: while idle
									nothing is running -- the auto-pause deleted stt and
									t_captions_put_settings takes the unrestricted path -- so a
									live-save note would be false there, and it must not promise
									the feed's return brings captions back either (auto-resume
									isn't built yet).
									What the live note says is DERIVED, not described -- see
									$liveRestartFields/$liveLockedFields, built off the same
									LIVE_EDITABLE_FIELDS/STT_RESTART_FIELDS sets that decide
									which controls are editable right now. The sentence this
									replaces ("Changes apply immediately. Some changes restart
									recognition and drop captions for a few seconds.") was one
									claim covering three different classes of field, naming
									neither the ones that cost a caption gap nor the reason half
									the form had gone grey. */}
									{$status.state === 'live' && (
										<Grid item xs={12}>
											<Typography className="captions-detail">
												<Trans>Changes apply to the running channel.</Trans>
												{$liveRestartFields.length > 0 && (
													<React.Fragment>
														{' '}
														<Trans>
															Saving a transcription setting restarts transcription — captions drop
															for a few seconds.
														</Trans>
													</React.Fragment>
												)}
											</Typography>
											{$liveLockedFields.length > 0 && (
												<Typography className="captions-detail">
													<Trans>
														Greyed-out settings can only be changed with captions off: switch captions
														off, change them, then switch them back on.
													</Trans>
												</Typography>
											)}
										</Grid>
									)}
									{$status.state === 'idle' && (
										<Grid item xs={12}>
											<Typography className="captions-detail">
												<Trans>Captions are paused. Changes will apply the next time captions start.</Trans>
											</Typography>
										</Grid>
									)}

									{/* Three groups of three to five, in the order the
									decisions are actually made -- what recognises the speech,
									what it looks like, where it comes out -- instead of ten
									controls in one flat run above an accordion that was the
									only part of the form with any structure. */}
									<Grid item xs={12}>
										<Typography className={`${classes.sectionLabel} first`}>
											<Trans>Transcription</Trans>
										</Typography>
									</Grid>
									<Grid item xs={12} title={lockedFieldTitle('transcription_provider')}>
										<Select
											label={<Trans>Transcription provider</Trans>}
											value={$settings.transcription_provider}
											onChange={handleProviderChange}
											disabled={fieldLockedWhileLive('transcription_provider')}
											renderValue={(value) => TRANSCRIPTION_PROVIDER_NAMES[value]}
										>
											<MenuItem value="deepgram" disabled={checked && currentlyBatchDiarized}>
												{providerOption('deepgram', <Trans>Fast · 88 languages</Trans>)}
											</MenuItem>
											<MenuItem value="google" disabled={checked && currentlyBatchDiarized}>
												{providerOption('google', <Trans>29 languages</Trans>)}
											</MenuItem>
											<MenuItem value="gemini" disabled={checked && currentlyBatchDiarized}>
												{providerOption('gemini', <Trans>Handles language switching · 29 languages</Trans>)}
											</MenuItem>
											<MenuItem value="speechmatics" disabled={checked && currentlyBatchDiarized}>
												{providerOption('speechmatics', <Trans>Speaker labels · 59 languages</Trans>)}
											</MenuItem>
											<MenuItem value="google_batch_diarized" disabled={checked && !currentlyBatchDiarized}>
												{providerOption('google_batch_diarized', <Trans>Speaker labels · runs well behind live</Trans>)}
											</MenuItem>
											{parakeetLocalOffered($settings.transcription_provider) && (
												<MenuItem value="parakeet_local" disabled={checked && currentlyBatchDiarized}>
													{providerOption('parakeet_local', <Trans>Runs locally · English only</Trans>)}
												</MenuItem>
											)}
										</Select>
									</Grid>
									{$settings.transcription_provider === 'google_batch_diarized' && (
										<Grid item xs={12}>
											<Typography className="captions-detail is-warning">
												<Trans>
													Captions will lag roughly {$settings.google_batch_delay_seconds} seconds behind the live video —
													that delay is what makes it possible to label who's speaking. Choose this only when knowing who
													said what matters more than getting captions on screen quickly.
												</Trans>
											</Typography>
										</Grid>
									)}
									{$settings.transcription_provider === 'speechmatics' && (
										<Grid item xs={12}>
											<TextField
												variant="outlined"
												fullWidth
												type="number"
												inputProps={{ min: 0.7, max: 4.0, step: 0.1 }}
												label={<Trans>Caption latency (seconds)</Trans>}
												helperText={<Trans>Lower is faster but corrects itself on screen more often; higher is slower but more stable. 0.7-4.0.</Trans>}
												{...numberFieldProps('speechmatics_max_delay')}
												disabled={fieldLockedWhileLive('speechmatics_max_delay')} title={lockedFieldTitle('speechmatics_max_delay')}
											/>
										</Grid>
									)}
									{$languageResetWarning && (
										<Grid item xs={12}>
											<Typography className="captions-detail is-warning">
												{$languageResetWarning.field === 'cc1_lang' ? (
													<Trans>
														Your caption language wasn't available on this provider — reset to {$languageResetWarning.label}. Choose a different one before saving.
													</Trans>
												) : (
													<Trans>
														Your second caption language wasn't recognized — reset to {$languageResetWarning.label}. Choose a different one before saving.
													</Trans>
												)}
											</Typography>
										</Grid>
									)}
									<Grid item xs={12}>
										<Autocomplete
											options={CC1_LANGUAGES_BY_PROVIDER[$settings.transcription_provider]}
											getOptionLabel={(option) => option.label}
											isOptionEqualToValue={(option, value) => option.code === value.code}
											value={CC1_LANGUAGES_BY_PROVIDER[$settings.transcription_provider].find((lang) => lang.code === $settings.cc1_lang) || null}
											onChange={handleCc1LangChange}
											// Autocomplete spreads unknown props onto its root, so the
											// tooltip lands on the field itself (a misc/Select would
											// drop it -- see lockedFieldTitle).
											disabled={fieldLockedWhileLive('cc1_lang') || geminiAutoLanguage}
											title={lockedFieldTitle('cc1_lang')}
											disableClearable
											renderInput={(params) => <TextField {...params} variant="outlined" label={<Trans>Spoken language</Trans>} />}
										/>
									</Grid>
									{/* Sits with the language picker it overrides, not in Advanced
									settings with the rest of the Gemini-only fields -- it is the one
									Gemini setting that changes what another visible control does. */}
									{$settings.transcription_provider === 'gemini' && (
										<Grid item xs={12}>
											<FormControlLabel
												title={lockedFieldTitle('gemini_auto_language')}
												control={
													<Checkbox
														checked={!!$settings.gemini_auto_language}
														onChange={handleCheckboxSettingToggle('gemini_auto_language')}
														size="small"
														disabled={fieldLockedWhileLive('gemini_auto_language')} title={lockedFieldTitle('gemini_auto_language')}
													/>
												}
												label={<Trans>Detect language automatically</Trans>}
											/>
											<FormHelperText>
												<Trans>
													The model identifies the spoken language itself, including a speaker who switches language
													mid-sentence. The caption language above still decides which caption channel the text appears
													on, so leave it set to the language you expect most of the time.
												</Trans>
											</FormHelperText>
										</Grid>
									)}

									<Grid item xs={12}>
										<Typography className={classes.sectionLabel}>
											<Trans>Caption appearance</Trans>
										</Typography>
									</Grid>
									<Grid item xs={$settings.caption_mode === 'roll-up' ? 6 : 12} title={lockedFieldTitle('caption_mode')}>
										<Select label={<Trans>On-screen style</Trans>} value={$settings.caption_mode} onChange={updateSetting('caption_mode')} disabled={fieldLockedWhileLive('caption_mode')}>
											<MenuItem value="pop-on">
												<Trans>Pop-on</Trans>
											</MenuItem>
											<MenuItem value="roll-up">
												<Trans>Roll-up</Trans>
											</MenuItem>
										</Select>
									</Grid>
									{$settings.caption_mode === 'roll-up' && (
										<Grid item xs={6} title={lockedFieldTitle('roll_up_rows')}>
											<Select label={<Trans>Rows</Trans>} value={$settings.roll_up_rows} onChange={updateSetting('roll_up_rows')} disabled={fieldLockedWhileLive('roll_up_rows')}>
												<MenuItem value={2}>2</MenuItem>
												<MenuItem value={3}>3</MenuItem>
												<MenuItem value={4}>4</MenuItem>
											</Select>
										</Grid>
									)}
									{/* alignItems on this row rather than leaning on
									formControlLabel.js's global marginTop: '-.3em', which is
									tuned for a single-line label and left the checkbox sitting
									level with the top of the box beside it. */}
									<Grid item xs={12}>
										<Grid container spacing={2} alignItems="center">
											<Grid item xs={6}>
												<TextField
													variant="outlined"
													fullWidth
													type="number"
													inputProps={{ min: 1, max: 15 }}
													label={<Trans>Row position</Trans>}
													helperText={<Trans>Row {$settings.start_row} of 15 — 1 is top, 15 is bottom.</Trans>}
													{...numberFieldProps('start_row')}
													disabled={fieldLockedWhileLive('start_row')} title={lockedFieldTitle('start_row')}
												/>
											</Grid>
											<Grid item xs={6}>
												<FormControlLabel
													title={lockedFieldTitle('all_caps')}
													control={<Checkbox checked={!!$settings.all_caps} onChange={handleAllCapsToggle} size="small" disabled={fieldLockedWhileLive('all_caps')} title={lockedFieldTitle('all_caps')} />}
													label={<Trans>ALL CAPS</Trans>}
												/>
											</Grid>
										</Grid>
									</Grid>
									{/* Promoted out of Advanced settings: both apply to every
									provider and are touched far more often than anything left in
									there, which was tiered by how deep the engineering went
									rather than by how often an operator reaches for it. */}
									<Grid item xs={12}>
										<TextField
											variant="outlined"
											fullWidth
											type="number"
											inputProps={{ min: 0, max: 10, step: 0.1 }}
											label={<Trans>Minimum display time (seconds)</Trans>}
											helperText={<Trans>Seconds a caption stays on screen before being replaced.</Trans>}
											{...numberFieldProps('min_display_seconds')}
											disabled={fieldLockedWhileLive('min_display_seconds')} title={lockedFieldTitle('min_display_seconds')}
										/>
									</Grid>
									<Grid item xs={12}>
										<TextField
											variant="outlined"
											fullWidth
											type="text"
											label={<Trans>Speaker marker</Trans>}
											helperText={<Trans>Shown when a new speaker starts talking. Leave blank to disable.</Trans>}
											value={$settings.speaker_change_indicator}
											onChange={updateSetting('speaker_change_indicator')}
											disabled={fieldLockedWhileLive('speaker_change_indicator')} title={lockedFieldTitle('speaker_change_indicator')}
										/>
									</Grid>

									<Grid item xs={12}>
										<Typography className={classes.sectionLabel}>
											<Trans>Delivery</Trans>
										</Typography>
									</Grid>
									{/* Moved down out of the language pair: its help text is about
									the second language and the tracks below it, and "which CC
									channel" is a once-per-channel decision, unlike the provider
									and language an operator comes back to. */}
									<Grid item xs={12} title={lockedFieldTitle('primary_caption_channel')}>
										<Select
											label={<Trans>Caption channel</Trans>}
											value={$settings.primary_caption_channel}
											onChange={updateSetting('primary_caption_channel')}
											disabled={fieldLockedWhileLive('primary_caption_channel')}
											helperText={<Trans>Which caption channel the recognized language is sent to. A second language, if enabled, uses the other channel.</Trans>}
										>
											<MenuItem value="cc1">
												<Trans>CC1 / Service 1</Trans>
											</MenuItem>
											<MenuItem value="cc3">
												<Trans>CC3 / Service 2</Trans>
											</MenuItem>
										</Select>
									</Grid>
									{/* The two CEA formats this channel puts in the video, as
									positive controls: checked means "they are going out". Its own
									heading, because this is a decision about the output FORMAT
									rather than about which slot the text lands in -- and because
									it is the only place in this form where two identical-looking
									controls behave differently while live (see the
									disable_608/disable_708 comment in DEFAULT_SETTINGS; do not
									"fix" the asymmetry). Labelled by standard only: operators in
									this market know what 608 and 708 mean, so the only helper text
									kept is the one operational fact they cannot infer from the
									control itself -- that 608 has to be set with captions off.
									That sentence is on purpose the same one the 608 control's own
									title= carries while it is locked (see lockedFieldTitle):
									visible help for the operator who reads the form, a hover for
									the one who only sees a greyed box. */}
									<Grid item xs={12}>
										<Typography className={classes.sectionLabel}>
											<Trans>Delivered formats</Trans>
										</Typography>
									</Grid>
									<Grid item xs={12}>
										<FormControlLabel
											title={lockedFieldTitle('disable_608')}
											control={
												<Checkbox
													checked={!$settings.disable_608}
													onChange={handleCaptionFormatToggle('disable_608')}
													size="small"
													disabled={fieldLockedWhileLive('disable_608')} title={lockedFieldTitle('disable_608')}
												/>
											}
											label={<Trans>CEA-608</Trans>}
										/>
										<FormHelperText>
											<Trans>Set with captions off, then switch them back on.</Trans>
										</FormHelperText>
									</Grid>
									<Grid item xs={12}>
										<FormControlLabel
											title={lockedFieldTitle('disable_708')}
											control={
												<Checkbox
													checked={!$settings.disable_708}
													onChange={handleCaptionFormatToggle('disable_708')}
													size="small"
													disabled={fieldLockedWhileLive('disable_708')} title={lockedFieldTitle('disable_708')}
												/>
											}
											label={<Trans>CEA-708</Trans>}
										/>
									</Grid>
									<Grid item xs={12}>
										<FormControlLabel
											title={lockedFieldTitle('cc3_lang')}
											control={<Checkbox checked={!!$settings.cc3_lang} onChange={handleSecondLanguageToggle} size="small" disabled={fieldLockedWhileLive('cc3_lang')} title={lockedFieldTitle('cc3_lang')} />}
											label={<Trans>Add a second caption language</Trans>}
										/>
									</Grid>
									{!!$settings.cc3_lang && (
										<Grid item xs={12}>
											<Autocomplete
												options={CC3_LANGUAGES}
												getOptionLabel={(option) => option.label}
												isOptionEqualToValue={(option, value) => option.code === value.code}
												value={CC3_LANGUAGES.find((lang) => lang.code === $settings.cc3_lang) || null}
												onChange={handleCc3LangChange}
												disabled={fieldLockedWhileLive('cc3_lang')} title={lockedFieldTitle('cc3_lang')}
												disableClearable
												renderInput={(params) => (
													<TextField {...params} variant="outlined" label={<Trans>Second caption language</Trans>} />
												)}
											/>
										</Grid>
									)}
									<Grid item xs={12}>
										<Autocomplete
											multiple
											options={CC3_LANGUAGES.filter((lang) => lang.code !== $settings.cc3_lang && lang.code !== $settings.cc1_lang.split('-')[0])}
											getOptionLabel={(option) => option.label}
											isOptionEqualToValue={(option, value) => option.code === value.code}
											value={($settings.caption_tracks || []).map((code) => CC3_LANGUAGES.find((lang) => lang.code === code) || { code, label: code })}
											getOptionDisabled={(option) => ($settings.caption_tracks || []).length >= 4 && !$settings.caption_tracks.includes(option.code)}
											onChange={(_, values) => setSettings({ ...$settings, caption_tracks: values.map((lang) => lang.code) })}
											disabled={fieldLockedWhileLive('caption_tracks')} title={lockedFieldTitle('caption_tracks')}
											renderInput={(params) => <TextField {...params} variant="outlined" label={<Trans>Additional caption languages</Trans>} helperText={<Trans>Up to four additional languages. The subtitle track preserves all scripts; embedded captions depend on character and receiver support.</Trans>} />}
										/>
									</Grid>
									<Grid item xs={12}>
										<FormControlLabel
											title={lockedFieldTitle('webvtt_captions_enabled')}
											control={
												<Checkbox
													checked={!!$settings.webvtt_captions_enabled}
													onChange={handleCheckboxSettingToggle('webvtt_captions_enabled')}
													size="small"
													disabled={fieldLockedWhileLive('webvtt_captions_enabled')} title={lockedFieldTitle('webvtt_captions_enabled')}
												/>
											}
											label={<Trans>Subtitle track</Trans>}
										/>
										<FormHelperText>
											<Trans>Adds a toggleable subtitle track viewers can turn on in their video player, in addition to standard closed captions.</Trans>
										</FormHelperText>
									</Grid>
									<Grid item xs={12}>
										<Accordion className="accordion">
											<AccordionSummary className="accordion-summary" elevation={0} expandIcon={<ArrowDropDownIcon />}>
												<Typography>
													<Trans>Advanced settings</Trans>
												</Typography>
											</AccordionSummary>
											<AccordionDetails>
												<Grid container spacing={2}>
													{$settings.transcription_provider === 'google' && (
														<React.Fragment>
															<Grid item xs={12}>
																<Typography className={classes.sectionLabel}>
																	<Trans>Transcription tuning</Trans>
																</Typography>
															</Grid>
															<Grid item xs={12}>
																<Autocomplete
																	multiple
																	freeSolo
																	options={[]}
																	value={$settings.google_boost_phrases}
																	onChange={handleTagListSettingChange('google_boost_phrases')}
																	disabled={fieldLockedWhileLive('google_boost_phrases')} title={lockedFieldTitle('google_boost_phrases')}
																	renderInput={(params) => (
																		<TextField
																			{...params}
																			variant="outlined"
																			label={<Trans>Boost phrases</Trans>}
																			placeholder={i18n._(t`Type a word or phrase, press Enter`)}
																			// Without this MUI keeps the label unshrunk over
																			// an empty free-solo field and suppresses the
																			// placeholder, so the control renders as a box
																			// containing the words "Boost phrases" -- an empty
																			// state impersonating a filled one, with its only
																			// instructions invisible.
																			InputLabelProps={{ shrink: true }}
																			helperText={
																				<Trans>
																					Improves recognition for words the model might otherwise miss — brand names, jargon, proper nouns.
																				</Trans>
																			}
																		/>
																	)}
																/>
															</Grid>
															<Grid item xs={12}>
																<TextField
																	variant="outlined"
																	fullWidth
												type="number"
																inputProps={{ min: 0, max: 20, step: 1 }}
																label={<Trans>Strength</Trans>}
																helperText={<Trans>How strongly to favor boost phrases. 0-20.</Trans>}
																{...numberFieldProps('google_boost_value')}
																disabled={fieldLockedWhileLive('google_boost_value')} title={lockedFieldTitle('google_boost_value')}
															/>
														</Grid>
														<Grid item xs={12}>
															<FormControlLabel
																title={lockedFieldTitle('google_profanity_filter')}
																control={
																	<Checkbox
																		checked={!!$settings.google_profanity_filter}
																		onChange={handleCheckboxSettingToggle('google_profanity_filter')}
																		size="small"
																		disabled={fieldLockedWhileLive('google_profanity_filter')} title={lockedFieldTitle('google_profanity_filter')}
																	/>
																}
																label={<Trans>Filter profanity</Trans>}
															/>
														</Grid>
														<Grid item xs={12}>
															<FormControlLabel
																title={lockedFieldTitle('google_enable_vad')}
																control={
																	<Checkbox
																		checked={!!$settings.google_enable_vad}
																		onChange={handleCheckboxSettingToggle('google_enable_vad')}
																		size="small"
																		disabled={fieldLockedWhileLive('google_enable_vad')} title={lockedFieldTitle('google_enable_vad')}
																	/>
																}
																label={<Trans>Skip silent audio</Trans>}
															/>
														</Grid>
													</React.Fragment>
												)}
													{$settings.transcription_provider === 'gemini' && (
														<React.Fragment>
															<Grid item xs={12}>
																<Typography className={classes.sectionLabel}>
																	<Trans>Transcription tuning</Trans>
																</Typography>
															</Grid>
															<Grid item xs={12} title={lockedFieldTitle('gemini_mode')}>
																<Select
																	label={<Trans>Transcription style</Trans>}
																	value={$settings.gemini_mode}
																	onChange={updateSetting('gemini_mode')}
																	disabled={fieldLockedWhileLive('gemini_mode')}
																>
																	<MenuItem value="VERBATIM">
																		<Trans>Verbatim</Trans>
																	</MenuItem>
																	<MenuItem value="SMART">
																		<Trans>Clean up speech</Trans>
																	</MenuItem>
																</Select>
																<Typography variant="caption" color="textSecondary" component="p">
																	<Trans>
																		Verbatim transcribes exactly what was said. Clean up speech removes filler words and
																		applies the speaker's own corrections — check your accuracy obligations before using it.
																	</Trans>
																</Typography>
															</Grid>
															<Grid item xs={12}>
																{/* Same control as Google's boost phrases above, minus the
																strength field: this API takes a plain term list with no
																per-phrase weight (see gemini_stt_client.py's
																--custom-vocabulary). */}
																<Autocomplete
																	multiple
																	freeSolo
																	options={[]}
																	value={$settings.gemini_custom_vocabulary}
																	onChange={handleTagListSettingChange('gemini_custom_vocabulary')}
																	disabled={fieldLockedWhileLive('gemini_custom_vocabulary')} title={lockedFieldTitle('gemini_custom_vocabulary')}
																	renderInput={(params) => (
																		<TextField
																			{...params}
																			variant="outlined"
																			label={<Trans>Boost phrases</Trans>}
																			placeholder={i18n._(t`Type a word or phrase, press Enter`)}
																			// Without this MUI keeps the label unshrunk over
																			// an empty free-solo field and suppresses the
																			// placeholder, so the control renders as a box
																			// containing the words "Boost phrases" -- an empty
																			// state impersonating a filled one, with its only
																			// instructions invisible.
																			InputLabelProps={{ shrink: true }}
																			helperText={
																				<Trans>
																					Improves recognition for words the model might otherwise miss — brand names, jargon, proper nouns.
																				</Trans>
																			}
																		/>
																	)}
																/>
															</Grid>
															<Grid item xs={12} title={lockedFieldTitle('gemini_speech_detection')}>
																<Select
																	label={<Trans>Speech detection</Trans>}
																	value={$settings.gemini_speech_detection}
																	onChange={updateSetting('gemini_speech_detection')}
																	disabled={fieldLockedWhileLive('gemini_speech_detection')}
																>
																	<MenuItem value="off">
																		<Trans>Off — transcribe everything</Trans>
																	</MenuItem>
																	<MenuItem value="auto">
																		<Trans>Automatic</Trans>
																	</MenuItem>
																</Select>
																<Typography variant="caption" color="textSecondary" component="p">
																	<Trans>
																		Off captions everything that is said, and is the right choice with crowd or background
																		noise, where automatic detection misses much of the speech. Automatic lets Gemini decide
																		what counts as speech, which suits a quiet studio.
																	</Trans>
																</Typography>
															</Grid>
															{$settings.gemini_speech_detection === 'auto' && (
															<Grid item xs={12} title={lockedFieldTitle('gemini_end_of_speech_sensitivity')}>
																<Select
																	label={<Trans>End-of-speech sensitivity</Trans>}
																	value={$settings.gemini_end_of_speech_sensitivity}
																	onChange={updateSetting('gemini_end_of_speech_sensitivity')}
																	disabled={fieldLockedWhileLive('gemini_end_of_speech_sensitivity')}
																>
																	<MenuItem value="HIGH">
																		<Trans>High</Trans>
																	</MenuItem>
																	<MenuItem value="LOW">
																		<Trans>Low</Trans>
																	</MenuItem>
																</Select>
																<Typography variant="caption" color="textSecondary" component="p">
																	<Trans>
																		How quickly a pause is treated as the end of a sentence. High puts captions on screen
																		sooner; Low waits longer, which suits a speaker who pauses mid-thought.
																	</Trans>
																</Typography>
															</Grid>
															)}
															<Grid item xs={12}>
																<FormControlLabel
																	title={lockedFieldTitle('gemini_forward_interims')}
																	control={
																		<Checkbox
																			checked={!!$settings.gemini_forward_interims}
																			onChange={handleCheckboxSettingToggle('gemini_forward_interims')}
																			size="small"
																			disabled={fieldLockedWhileLive('gemini_forward_interims')} title={lockedFieldTitle('gemini_forward_interims')}
																		/>
																	}
																	label={<Trans>Low-latency interim captions (recommended)</Trans>}
																/>
																<Typography variant="caption" color="textSecondary" component="p">
																	<Trans>
																		Puts words on screen as they are recognized instead of waiting for the speaker to
																		pause. Leave this on: without it, captions for someone who talks without pausing can
																		lag far behind the video. The cost is that a word is occasionally corrected on screen.
																	</Trans>
																</Typography>
															</Grid>
														</React.Fragment>
													)}
													{['google', 'gemini', 'speechmatics'].includes($settings.transcription_provider) && (
														<React.Fragment>
															{/* Deliberately google + gemini + speechmatics, NOT google_batch_diarized -- a
															different pairing to the "Audio processing" section below, because
															this is the set of providers whose STT path can actually isolate
															(restreamer_captions.VOCALS_ISOLATION_PROVIDERS).
															Reprovision-class: see google_isolate_vocals' own comment in
															DEFAULT_SETTINGS above. */}
														<Grid item xs={12}>
																<Typography className={classes.sectionLabel}>
																	<Trans>Vocals isolation</Trans>
																</Typography>
														</Grid>
														<Grid item xs={12}>
																<FormControlLabel
																	title={lockedFieldTitle('google_isolate_vocals')}
																	control={
																		<Checkbox
																			checked={!!$settings.google_isolate_vocals}
																			onChange={handleCheckboxSettingToggle('google_isolate_vocals')}
																			size="small"
																			disabled={fieldLockedWhileLive('google_isolate_vocals')} title={lockedFieldTitle('google_isolate_vocals')}
																		/>
																	}
																	label={<Trans>Separate the announcer's voice from music and crowd noise</Trans>}
																/>
																<FormHelperText>
																	<Trans>
																		Runs a voice-separation model ahead of recognition, so commentary still comes
																		through when it is competing with stadium noise, music or a crowd. It costs more
																		processing and adds a moment of delay before captions appear. Because it changes
																		the channel's own audio chain, it can only be changed while captions are off:
																		switch captions off, set this, then switch them back on.
																	</Trans>
																</FormHelperText>
														</Grid>
														</React.Fragment>
													)}
													{['google', 'google_batch_diarized'].includes($settings.transcription_provider) && (
														<React.Fragment>
															{/* Shared by both Google-based providers, not duplicated into
															each one's own section above/below -- RecognitionConfig.
															denoiser_config lives on the request's top-level config, not
															inside RecognitionFeatures, so it isn't scoped to just the
															real-time provider the way boost_phrases/profanity_filter/
															enable_vad are (see google_enable_denoise's own comment in
															DEFAULT_SETTINGS above). */}
															<Grid item xs={12}>
																<Typography className={classes.sectionLabel}>
																	<Trans>Audio processing</Trans>
																</Typography>
															</Grid>
															<Grid item xs={12}>
																<FormControlLabel
																	title={lockedFieldTitle('google_enable_denoise')}
																	control={
																		<Checkbox
																			checked={!!$settings.google_enable_denoise}
																			onChange={handleCheckboxSettingToggle('google_enable_denoise')}
																			size="small"
																			disabled={fieldLockedWhileLive('google_enable_denoise')} title={lockedFieldTitle('google_enable_denoise')}
																		/>
																	}
																	label={<Trans>Reduce background noise</Trans>}
																/>
															</Grid>
															{$settings.google_enable_denoise && (
																<Grid item xs={12}>
																	<TextField
																		variant="outlined"
																		fullWidth
																		type="number"
																		inputProps={{ min: 0, step: 0.1 }}
																		label={<Trans>Background noise threshold</Trans>}
																		helperText={
																			<Trans>
																				Higher values filter more aggressively but risk dropping quiet speech. Start at 0
																				(off); raise only if noisy audio is producing garbled captions.
																			</Trans>
																		}
																		{...numberFieldProps('google_denoise_snr_threshold')}
																		disabled={fieldLockedWhileLive('google_denoise_snr_threshold')} title={lockedFieldTitle('google_denoise_snr_threshold')}
																	/>
																</Grid>
															)}
														</React.Fragment>
													)}
													{$settings.transcription_provider === 'google_batch_diarized' && (
														<React.Fragment>
															<Grid item xs={12}>
																<Typography className={classes.sectionLabel}>
																	<Trans>Speaker diarization timing</Trans>
																</Typography>
															</Grid>
															<Grid item xs={12}>
																<Typography className="captions-detail">
																	<Trans>
																		Audio is analyzed in windows, then captions are held back so they land in sync
																		with the delayed video. The delay must comfortably cover one window plus
																		processing time, or captions that arrive too late are dropped rather than
																		shown out of sync.
																	</Trans>
																</Typography>
															</Grid>
															{/* Stacked full-width (xs={12} only), not sm={4}/three-across --
															MUI's sm breakpoint responds to the browser's own viewport width,
															not this sidebar panel's real rendered width (confirmed live: at
															a normal page width, sm={4} still triggered here even though the
															panel itself renders at ~280px, truncating every one of these
															three labels unreadably -- "Captio...", "Analys...", "Safety …").
															One field per row avoids that regardless of viewport. */}
															<Grid item xs={12}>
																<TextField
																	variant="outlined"
																	fullWidth
																	type="number"
																	inputProps={{ min: 0, step: 1 }}
																	label={<Trans>Caption delay (seconds)</Trans>}
																	helperText={<Trans>How far behind live video the captions run.</Trans>}
																	{...numberFieldProps('google_batch_delay_seconds')}
																	disabled={fieldLockedWhileLive('google_batch_delay_seconds')} title={lockedFieldTitle('google_batch_delay_seconds')}
																/>
															</Grid>
															<Grid item xs={12}>
																<TextField
																	variant="outlined"
																	fullWidth
																	type="number"
																	inputProps={{ min: 1, max: 55, step: 1 }}
																	label={<Trans>Analysis window (seconds)</Trans>}
																	helperText={<Trans>How much audio is analyzed together. Max 55.</Trans>}
																	{...numberFieldProps('google_batch_chunk_seconds')}
																	disabled={fieldLockedWhileLive('google_batch_chunk_seconds')} title={lockedFieldTitle('google_batch_chunk_seconds')}
																/>
															</Grid>
															<Grid item xs={12}>
																<TextField
																	variant="outlined"
																	fullWidth
																	type="number"
																	inputProps={{ min: 0, step: 1 }}
																	label={<Trans>Safety margin (seconds)</Trans>}
																	helperText={<Trans>Extra buffer for slow processing before a caption is dropped.</Trans>}
																	{...numberFieldProps('google_batch_safety_margin_seconds')}
																	disabled={fieldLockedWhileLive('google_batch_safety_margin_seconds')} title={lockedFieldTitle('google_batch_safety_margin_seconds')}
																/>
															</Grid>
														</React.Fragment>
													)}
													{$settings.transcription_provider !== 'google_batch_diarized' && (
														<React.Fragment>
															<Grid item xs={12}>
																<Typography className={classes.sectionLabel}>
																	<Trans>Video delay</Trans>
																</Typography>
															</Grid>
															<Grid item xs={12}>
																<TextField
																	variant="outlined"
																	fullWidth
																	type="number"
																	inputProps={{ min: 0, max: 60, step: 0.5 }}
																	label={<Trans>Video delay (seconds)</Trans>}
																	helperText={
																		<Trans>
																			Holds the video and audio back so captions line up with the picture. Viewers
																			see the stream this much later. 0 turns it off. Max 60.
																		</Trans>
																	}
																	{...numberFieldProps('video_delay_seconds')}
																	disabled={fieldLockedWhileLive('video_delay_seconds')}
																	title={lockedFieldTitle('video_delay_seconds')}
																/>
															</Grid>
														</React.Fragment>
													)}
													{/* Card #54: local (self-hosted) speaker diarization. Mutually
													exclusive by provider with "Speaker diarization timing"
													immediately above, so the two "Speaker diarization*"
													sections never render together -- that one belongs to the
													provider that diarizes in the cloud, this one to the two
													providers that cannot. The gate is
													LOCAL_DIARIZATION_PROVIDERS above, and it mirrors a hard
													server rule rather than stating this panel's own policy:
													t_captions_enable runs validate_local_diarization against
													the merged config and answers any other pairing with an
													HTTP 400, so a control offered here for
													deepgram/speechmatics/google_batch_diarized would only
													ever be a save that fails at enable time.
													The else branch says why, instead of leaving the operator
													hunting for a setting the release notes mention -- and
													because the server derives its allowlist as "supported
													MINUS PROVIDERS_WITH_NATIVE_DIARIZATION", every provider
													that lands in that branch is one that produces speaker
													labels of its own, which is exactly what the line says. */}
													{LOCAL_DIARIZATION_PROVIDERS.includes($settings.transcription_provider) ? (
														<React.Fragment>
															<Grid item xs={12}>
																<Typography className={classes.sectionLabel}>
																	<Trans>Speaker diarization</Trans>
																</Typography>
															</Grid>
															<Grid item xs={12}>
																<FormControlLabel
																	title={lockedFieldTitle('local_diarize')}
																	control={
																		<Checkbox
																			checked={!!$settings.local_diarize}
																			onChange={handleCheckboxSettingToggle('local_diarize')}
																			size="small"
																			disabled={fieldLockedWhileLive('local_diarize')} title={lockedFieldTitle('local_diarize')}
																		/>
																	}
																	label={<Trans>Local diarization (self-hosted)</Trans>}
																/>
																<FormHelperText>
																	<Trans>
																		Labels speakers using a diarizer running on this stack, for
																		providers whose own streaming recognition returns no speaker
																		information at all. A self-hosted diarizer is only used with
																		providers that have no diarization of their own, so this is
																		offered for Google and Gemini only; the rest label their own
																		speakers, and those are the labels used. Because it starts or
																		stops a process rather than re-tuning a running one, it can
																		only be changed while captions are off: switch captions off,
																		set this, then switch them back on.
																	</Trans>
																</FormHelperText>
															</Grid>
															{$settings.local_diarize && (
																<Grid item xs={12}>
																	<TextField
																		variant="outlined"
																		fullWidth
																		type="text"
																		label={<Trans>Diarizer endpoint</Trans>}
																		placeholder="192.168.139.3:8791"
																		// Same reason both Autocompletes in this accordion set
																		// it: MUI keeps an unshrunk label sitting over an empty
																		// field and suppresses the placeholder underneath it, so
																		// the only example of the format to type would be
																		// invisible exactly while the field is empty.
																		InputLabelProps={{ shrink: true }}
																		helperText={
																			<Trans>
																				Host and port the diarizer listens on, as the stream's container reaches it.
																				Leave blank to use this stack's diarizer.
																				127.0.0.1 and localhost cannot reach it from there and also mean this stack's
																				diarizer.
																			</Trans>
																		}
																		value={$settings.local_diarize_endpoint}
																		onChange={updateSetting('local_diarize_endpoint')}
																		disabled={fieldLockedWhileLive('local_diarize_endpoint')} title={lockedFieldTitle('local_diarize_endpoint')}
																	/>
																</Grid>
															)}
														</React.Fragment>
													) : (
														<Grid item xs={12}>
															<Typography className="captions-detail">
																<Trans>
																	Speaker labels come from the transcription provider itself for this
																	provider, so there is nothing to set here: a self-hosted diarizer
																	is only used with providers that have no diarization of their own.
																</Trans>
															</Typography>
														</Grid>
													)}
													{$settings.transcription_provider === 'speechmatics' && (
														<React.Fragment>
															<Grid item xs={12}>
																<Typography className={classes.sectionLabel}>
																	<Trans>Transcription tuning</Trans>
																</Typography>
															</Grid>
															<Grid item xs={12}>
																<Autocomplete
																	multiple
																	freeSolo
																	options={[]}
																	value={$settings.speechmatics_additional_vocab}
																	onChange={handleTagListSettingChange('speechmatics_additional_vocab')}
																	disabled={fieldLockedWhileLive('speechmatics_additional_vocab')} title={lockedFieldTitle('speechmatics_additional_vocab')}
																	renderInput={(params) => (
																		<TextField
																			{...params}
																			variant="outlined"
																			label={<Trans>Additional vocabulary</Trans>}
																			placeholder={i18n._(t`Type a word or phrase, press Enter`)}
																			// Without this MUI keeps the label unshrunk over
																			// an empty free-solo field and suppresses the
																			// placeholder, so the control renders as a box
																			// containing the words "Boost phrases" -- an empty
																			// state impersonating a filled one, with its only
																			// instructions invisible.
																			InputLabelProps={{ shrink: true }}
																			helperText={
																				<Trans>Words or phrases to recognize that aren't in the standard dictionary.</Trans>
																			}
																		/>
																	)}
																/>
															</Grid>
															<Grid item xs={12}>
																<FormControlLabel
																	title={lockedFieldTitle('speechmatics_diarize')}
																	control={
																		<Checkbox
																			checked={!!$settings.speechmatics_diarize}
																			onChange={handleCheckboxSettingToggle('speechmatics_diarize')}
																			size="small"
																			disabled={fieldLockedWhileLive('speechmatics_diarize')} title={lockedFieldTitle('speechmatics_diarize')}
																		/>
																	}
																	label={<Trans>Distinguish between speakers</Trans>}
																/>
															</Grid>
															{$settings.speechmatics_diarize && (
																<Grid item xs={12}>
																	<TextField
																		variant="outlined"
																		fullWidth
																		type="number"
																		inputProps={{ min: 1, max: 100 }}
																		label={<Trans>Max speakers</Trans>}
																		helperText={<Trans>Upper bound on distinct speakers tracked. 1-100.</Trans>}
																		{...numberFieldProps('speechmatics_max_speakers')}
																		disabled={fieldLockedWhileLive('speechmatics_max_speakers')} title={lockedFieldTitle('speechmatics_max_speakers')}
																	/>
																</Grid>
															)}
															<Grid item xs={12}>
																<FormControlLabel
																	title={lockedFieldTitle('speechmatics_enable_audio_events')}
																	control={
																		<Checkbox
																			checked={!!$settings.speechmatics_enable_audio_events}
																			onChange={handleCheckboxSettingToggle('speechmatics_enable_audio_events')}
																			size="small"
																			disabled={fieldLockedWhileLive('speechmatics_enable_audio_events')} title={lockedFieldTitle('speechmatics_enable_audio_events')}
																		/>
																	}
																	label={<Trans>Detect background sounds</Trans>}
																/>
															</Grid>
															{$settings.speechmatics_enable_audio_events && (
																<Grid item xs={12}>
																	<Autocomplete
																		multiple
																		options={SPEECHMATICS_AUDIO_EVENT_TYPES}
																		getOptionLabel={speechmaticsAudioEventTypeLabel}
																		value={$settings.speechmatics_audio_event_types}
																		onChange={handleTagListSettingChange('speechmatics_audio_event_types')}
																		disabled={fieldLockedWhileLive('speechmatics_audio_event_types')} title={lockedFieldTitle('speechmatics_audio_event_types')}
																		renderInput={(params) => (
																			<TextField
																				{...params}
																				variant="outlined"
																				label={<Trans>Sound types</Trans>}
																				helperText={
																					<Trans>
																						Tags laughter, applause, and music as their own captions, e.g. "[LAUGHTER]". Leave empty to
																						detect all types.
																					</Trans>
																				}
																			/>
																		)}
																	/>
																</Grid>
															)}
														</React.Fragment>
													)}
													{/* The heading stays even where the answer is "nothing
													here", so this accordion has the same anchors for every
													provider -- and the second line says what the operator
													CAN do instead. */}
													{$settings.transcription_provider === 'deepgram' && (
														<React.Fragment>
															<Grid item xs={12}>
																<Typography className={classes.sectionLabel}>
																	<Trans>Transcription tuning</Trans>
																</Typography>
															</Grid>
															<Grid item xs={12}>
																<Typography className="captions-detail">
																	<Trans>
																		Deepgram is tuned by Airchain and has no per-channel options. The source setting
																		below applies to every provider.
																	</Trans>
																</Typography>
															</Grid>
														</React.Fragment>
													)}
													{/* Min. display time and Speaker marker used to live here.
													They apply to every provider and are touched far more often
													than anything else in this accordion, so they sit with the
													rest of "what the captions look like" in the main form --
													Advanced is for provider-specific tuning. */}
													<Grid item xs={12}>
														<Typography className={classes.sectionLabel}>
															<Trans>Source</Trans>
														</Typography>
													</Grid>
													<Grid item xs={12}>
														<TextField
															variant="outlined"
															fullWidth
															type="number"
															inputProps={{ min: 0, step: 1 }}
															label={<Trans>Pause when no source for (minutes)</Trans>}
															helperText={
																<Trans>
																	Minutes without a source before captions pause themselves, so an idle channel costs nothing.
																	0 = never.
																</Trans>
															}
															{...numberFieldProps('idle_pause_seconds')}
															disabled={fieldLockedWhileLive('idle_pause_seconds')} title={lockedFieldTitle('idle_pause_seconds')}
														/>
													</Grid>
													<Grid item xs={12}>
														<FormControlLabel
															title={lockedFieldTitle('video_backoff_enabled')}
															control={<Checkbox checked={!!$settings.video_backoff_enabled} onChange={handleCheckboxSettingToggle('video_backoff_enabled')} size="small" disabled={fieldLockedWhileLive('video_backoff_enabled')} title={lockedFieldTitle('video_backoff_enabled')} />}
															label={<Trans>Back off video pipeline when source is unavailable</Trans>}
														/>
														<Typography className="captions-detail"><Trans>Stops repeated source retries after the grace period, then retries with increasing delays.</Trans></Typography>
													</Grid>
													{$settings.video_backoff_enabled && (
														<React.Fragment>
															<Grid item xs={12} md={4}><TextField variant="outlined" fullWidth type="number" inputProps={{ min: 1, max: 60, step: 1 }} label={<Trans>Back off after (minutes)</Trans>} {...numberFieldProps('video_backoff_idle_seconds')} disabled={fieldLockedWhileLive('video_backoff_idle_seconds')} title={lockedFieldTitle('video_backoff_idle_seconds')} /></Grid>
															<Grid item xs={12} md={4}><TextField variant="outlined" fullWidth type="number" inputProps={{ min: 5, max: 3600, step: 1 }} label={<Trans>First retry (seconds)</Trans>} {...numberFieldProps('video_backoff_initial_retry_seconds')} disabled={fieldLockedWhileLive('video_backoff_initial_retry_seconds')} title={lockedFieldTitle('video_backoff_initial_retry_seconds')} /></Grid>
															<Grid item xs={12} md={4}><TextField variant="outlined" fullWidth type="number" inputProps={{ min: 5, max: 1440, step: 1 }} label={<Trans>Maximum retry (minutes)</Trans>} {...numberFieldProps('video_backoff_max_retry_seconds')} disabled={fieldLockedWhileLive('video_backoff_max_retry_seconds')} title={lockedFieldTitle('video_backoff_max_retry_seconds')} /></Grid>
														</React.Fragment>
													)}
												</Grid>
											</AccordionDetails>
										</Accordion>
									</Grid>
								</Grid>
								{/* Everything about the save lives together at the bottom of
								the card and stays there while the form scrolls: what will
								happen, what did happen, and the two ways out. The failure
								message used to sit roughly 1200px up, between the accordion
								and the button it was talking about. */}
								<Stack className={classes.actionBar} direction="column" spacing={0.5}>
									{$settingsError !== null && (
										<Typography className="captions-detail is-error">
											{$settingsError.detail ? $settingsError.detail : <Trans>Couldn't save settings. Try again.</Trans>}
										</Typography>
									)}
									{restartsRecognitionOnSave && (
										<Typography className="captions-detail is-warning">
											<Trans>Saving restarts recognition. Captions will drop out for a few seconds.</Trans>
										</Typography>
									)}
									<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
										{$settingsSaving ? (
											<Typography className="captions-detail">
												<Trans>Saving…</Trans>
											</Typography>
										) : hasUnsavedChanges ? (
											<Typography className="captions-detail">
												<Trans>Unsaved changes — closing this panel without saving will discard them.</Trans>
											</Typography>
										) : $lastSaved ? (
											// A timestamp rather than a checkmark: it is worth more
											// to whoever reconciles this against a log afterwards.
											<Typography className="captions-detail">
												{$lastSaved.tracksChanged ? (
													<Trans>Saved {$lastSaved.time} — reload the player to pick up the new languages.</Trans>
												) : (
													<Trans>Saved {$lastSaved.time}</Trans>
												)}
											</Typography>
										) : (
											<span />
										)}
										<Stack direction="row" spacing={1}>
											<Button size="small" variant="outlined" color="default" disabled={$settingsSaving || !hasUnsavedChanges} onClick={discardChanges}>
												<Trans>Revert</Trans>
											</Button>
											{/* Disabled rather than a silent no-op: an unchanged
											save returned early and produced no response at all,
											which during a broadcast reads as a hung UI. */}
											<Button size="small" variant="outlined" color="primary" disabled={$settingsSaving || !hasUnsavedChanges} onClick={saveSettings}>
												<Trans>Save</Trans>
											</Button>
										</Stack>
									</Stack>
								</Stack>
							</React.Fragment>
						)}
					</Grid>
				)}
			</Grid>
		</Paper>
	);
}

Captions.defaultProps = {
	channelid: '',
	restreamer: null,
};
